import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Modal,
    TextField,
    Button,
    Grid,
    MenuItem,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
    Snackbar,
    Alert,
    Divider,
    Paper,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tabs,
    Tab,
    Stepper,
    Step,
    StepLabel,
    Switch,
    FormControlLabel,
    Checkbox,
    ToggleButton,
    ToggleButtonGroup,
    Collapse
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveIcon from "@mui/icons-material/Remove";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HotelIcon from '@mui/icons-material/Hotel';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EditIcon from '@mui/icons-material/Edit';
import { RoomService } from '../api/services/roomService';
import { ReservationService } from '../api/services/reservationService';
import { RateService } from '../api/services/rateService';
import { calculateFinalRoomPrice } from '../utils/pricingCalculator';
import { SettingsService } from '../api/services/settingsService';
import { GuestService } from '../api/services/guestService';
import { MediaService } from '../api/services/mediaService';
import { useSelector } from 'react-redux';
import { getCookie } from '../utils/cookies';
import { useCurrency, formatCurrency } from '../utils/currency';
import { isUaeProperty, calculateDubaiHospitalityTax, extractUaeCustomRates, DUBAI_DEFAULT_RATES } from '../utils/dubaiHospitalityTax';
import { SUPPORTED_PHONE_CODES, validatePhoneByCode, getMinPhoneLength } from '../utils/phoneValidation';

const isPastDate = (dateString) => new Date(dateString).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);

const calculateRoomUaeTaxBreakdown = (netBase, nights = 1, activeTaxes = [], hotelMeta = {}, isExempt = false) => {
    if (isExempt) {
        return {
            serviceCharge: 0,
            municipalityFee: 0,
            vat: 0,
            tourismDirham: 0,
            totalTax: 0,
            serviceChargePct: 10,
            municipalityFeePct: 7,
            vatPct: 5,
            tourismDirhamRate: 15
        };
    }
    const customRates = extractUaeCustomRates(activeTaxes);
    const uae = calculateDubaiHospitalityTax({
        netBaseAmount: Math.max(0, Number(netBase) || 0),
        nights: Math.max(1, Number(nights) || 1),
        bedroomsCount: 1,
        customRates,
        rating: hotelMeta.rating || 4,
        hotelType: hotelMeta.type || ''
    });
    return {
        serviceCharge: uae.serviceCharge,
        municipalityFee: uae.municipalityFee,
        vat: uae.vat,
        tourismDirham: uae.tourismDirham,
        totalTax: uae.totalTax,
        serviceChargePct: uae.serviceChargePct,
        municipalityFeePct: uae.municipalityFeePct,
        vatPct: uae.vatPct,
        tourismDirhamRate: uae.tourismDirhamRate
    };
};

const calculateDynamicTax = (activeTaxes, targetRooms, dailyRatesByRoom, taxExempt, overrideMode = 'Daily', roomsArr = [], isUae = false, hotelMeta = {}) => {
    if (taxExempt) return 0;
    if (isUae) {
        let totalTax = 0;
        targetRooms.forEach(idx => {
            const days = dailyRatesByRoom[idx] || [];
            const nights = days.length || 1;
            const rNet = days.reduce((s, d) => s + Number(overrideMode === 'Daily' && d.overrideRate !== '' ? d.overrideRate : d.baseRate), 0);
            const uae = calculateRoomUaeTaxBreakdown(rNet, nights, activeTaxes, hotelMeta, false);
            totalTax += uae.totalTax;
        });
        return Number(totalTax.toFixed(2));
    }
    let totalTax = 0;
    targetRooms.forEach(idx => {
        const days = dailyRatesByRoom[idx] || [];
        const roomObj = Array.isArray(roomsArr) ? roomsArr[idx] : null;
        const extraBedCharge = Number(roomObj?.extraBedCharge || (roomObj?.extraBed ? (roomObj?.extraBedPrice || roomObj?.extraBedRate || 0) : 0));

        days.forEach(d => {
            // GRV = RoomBaseRate + ExtraBedCharge (Declared/Base rate before discount)
            const baseRate = Number(d.baseRate || 0);
            const extraCharge = d.extraBedCharge !== undefined ? Number(d.extraBedCharge) : extraBedCharge;
            const grv = baseRate + extraCharge;

            // Action: Evaluate the GRV against the active tax tiers to find the Tax Percentage. This locks the T_slab (e.g., 18).
            let matchingTax = null;
            for (const t of activeTaxes) {
                if (t.serviceType === 'room' || t.serviceType === 'All' || t.serviceType === 'accommodation' || t.applicableTo === 'Room' || t.applicableTo === 'All') {
                    const min = t.minThreshold ? Number(t.minThreshold) : 0;
                    const max = t.maxThreshold ? Number(t.maxThreshold) : Infinity;
                    if (grv >= min && grv <= max) {
                        matchingTax = t;
                        break;
                    }
                }
            }

            // Scenario A: "Net Discount" (Amount off the Room Rate)
            // 1. Calculate New Net Taxable Value (NTV): NTV = GRV - D_net
            const ntv = Number(overrideMode === 'Daily' && d.overrideRate !== '' ? d.overrideRate : grv);

            // 2. Calculate the Final Tax Amount: Tax = NTV * (T_slab / 100)
            if (matchingTax) {
                totalTax += (ntv * Number(matchingTax.gstPct || matchingTax.rate || 0)) / 100;
            }
        });
    });
    return Number(totalTax.toFixed(2));
};

const calculateRoomTaxFromOverride = (activeTaxes, roomIdx, overrideAmount, dailyRatesByRoom, taxExempt, roomsArr = [], isUae = false, hotelMeta = {}) => {
    if (taxExempt) return 0;
    if (isUae) {
        const days = dailyRatesByRoom[roomIdx] || [];
        const nights = days.length || 1;
        const uae = calculateRoomUaeTaxBreakdown(overrideAmount, nights, activeTaxes, hotelMeta, false);
        return Number(uae.totalTax.toFixed(2));
    }
    const days = dailyRatesByRoom[roomIdx] || [];
    const roomObj = Array.isArray(roomsArr) ? roomsArr[roomIdx] : null;
    const extraBedCharge = Number(roomObj?.extraBedCharge || (roomObj?.extraBed ? (roomObj?.extraBedPrice || roomObj?.extraBedRate || 0) : 0));
    const roomBaseRate = days.reduce((s, d) => s + Number(d.baseRate || 0), 0);

    let totalTax = 0;
    days.forEach(d => {
        const baseRate = Number(d.baseRate || 0);
        const extraCharge = d.extraBedCharge !== undefined ? Number(d.extraBedCharge) : extraBedCharge;
        const grv = baseRate + extraCharge;

        let matchingTax = null;
        for (const t of activeTaxes) {
            if (t.serviceType === 'room' || t.serviceType === 'All' || t.serviceType === 'accommodation' || t.applicableTo === 'Room' || t.applicableTo === 'All') {
                const min = t.minThreshold ? Number(t.minThreshold) : 0;
                const max = t.maxThreshold ? Number(t.maxThreshold) : Infinity;
                if (grv >= min && grv <= max) {
                    matchingTax = t;
                    break;
                }
            }
        }

        const dOverride = roomBaseRate > 0 ? (Number(overrideAmount) * (baseRate / roomBaseRate)) : (Number(overrideAmount) / (days.length || 1));
        if (matchingTax) {
            totalTax += (dOverride * Number(matchingTax.gstPct || matchingTax.rate || 0)) / 100;
        }
    });
    return Number(totalTax.toFixed(2));
};

export default function GroupReservationFormModal({ open, onClose, onSuccess, editData }) {
    const { currency, currencySymbol } = useCurrency();
    const authData = JSON.parse(getCookie('adminAuth') || '{}');
    const activeTenant = useSelector((state) => {
        const tenants = state.auth?.tenants || [];
        const activeId = state.auth?.activeTenantId;
        return tenants.find(t => (t._id || t.id) === activeId) || tenants[0] || null;
    });

    // Check UAE context thoroughly (Redux activeTenant, authData hotel/property, country, city, or AED currency)
    const isUae = Boolean(
        currency === 'AED' ||
        activeTenant?.country === 'United Arab Emirates' ||
        activeTenant?.country === 'UAE' ||
        activeTenant?.city === 'Dubai' ||
        activeTenant?.currency === 'AED' ||
        isUaeProperty(activeTenant) ||
        isUaeProperty(authData?.tenant) ||
        isUaeProperty(authData?.hotel) ||
        isUaeProperty(authData?.property) ||
        authData?.hotel?.country === 'United Arab Emirates' ||
        authData?.hotel?.country === 'UAE' ||
        authData?.hotel?.city === 'Dubai'
    );
    const hotel = activeTenant || authData?.hotel || authData?.property || {};
    const tenantId = authData.tenantId || activeTenant?._id || activeTenant?.id || '1';

    const [rooms, setRooms] = useState([]);
    const [ratePlansByRoomType, setRatePlansByRoomType] = useState({});
    const [activeTaxes, setActiveTaxes] = useState([]);
    const [isLoadingRooms, setIsLoadingRooms] = useState(false);
    const [isLoadingRates, setIsLoadingRates] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPrefilling, setIsPrefilling] = useState(false);

    // Controlled Form State
    const [bookingSource, setBookingSource] = useState('');
    const [corporateName, setCorporateName] = useState('');
    const [showCorporateMatchModal, setShowCorporateMatchModal] = useState(false);

    const [existingCorporateMatch, setExistingCorporateMatch] = useState(null);
    const [manualSources, setManualSources] = useState([]);
    const [selectedManualSource, setSelectedManualSource] = useState('');
    const [groupName, setGroupName] = useState('');
    const [organiserName, setOrganiserName] = useState('');
    const [organiserEmail, setOrganiserEmail] = useState('');
    const [organiserPhone, setOrganiserPhone] = useState('');
    const [organiserPhoneCode, setOrganiserPhoneCode] = useState(isUae ? '+971' : '+91');
    const [checkIn, setCheckIn] = useState('');
    const [checkOut, setCheckOut] = useState('');

    // Rooms Array with nested guests
    const initialGuestState = {
        firstName: '', lastName: '', email: '', phone: '', phoneCode: isUae ? '+971' : '+91',
        address: '', zip: '', country: '', state: '', city: '',
        idProofFile: null, idProofPreviewUrl: null, idNumber: '', idType: '', nationality: ''
    };

    const initialKidState = {
        firstName: '', lastName: '', age: '', dob: '',
        idProofFile: null, idProofPreviewUrl: null
    };

    const [activeStep, setActiveStep] = useState(0);

    const [roomsArr, setRoomsArr] = useState([
        {
            roomId: '',
            ratePlanId: '',
            finalRate: '',
            rateAmount: '',
            adultsCount: 1,
            kidsCount: 0,
            extraBed: false,
            guests: [{ ...initialGuestState }],
            kids: []
        }
    ]);

        const [reservationFinalRate, setReservationFinalRate] = useState('');
    const [discounts, setDiscounts] = useState('');

    const [roomSubTotal, setRoomSubTotal] = useState('0.00');
    const [taxTotal, setTaxTotal] = useState('0.00');
    const [totalAmount, setTotalAmount] = useState('0.00');
    const [taxExempt, setTaxExempt] = useState(false);

    // Group Rate Management State
    const [overrideMode, setOverrideMode] = useState('Daily');
    const [selectedRateRoomIndex, setSelectedRateRoomIndex] = useState(0);
    const [dailyRatesByRoom, setDailyRatesByRoom] = useState({});
    const [originalDailyRatesByRoom, setOriginalDailyRatesByRoom] = useState({});
    const [enableBulkRateOverride, setEnableBulkRateOverride] = useState(false);
    const [overrideReasons, setOverrideReasons] = useState({});
    const [bulkDiscountType, setBulkDiscountType] = useState('Percentage');
    const [bulkDiscount, setBulkDiscount] = useState('');
    const [bulkApplyScope, setBulkApplyScope] = useState('This Room Only');
    const [appliedBulkByRoom, setAppliedBulkByRoom] = useState({});
    const [billingMode, setBillingMode] = useState('Master Bill');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
    const [advancePaid, setAdvancePaid] = useState('');

    // Guest Modal State
    const [showGuestModal, setShowGuestModal] = useState(false);
    const [activeRoomIndexForGuestModal, setActiveRoomIndexForGuestModal] = useState(0);
    const [activeGuestTab, setActiveGuestTab] = useState(0);

    // Guest Search State
    const [guestOptions, setGuestOptions] = useState([]);
    const [guestSearchLoading, setGuestSearchLoading] = useState(false);
    const [guestInputValue, setGuestInputValue] = useState('');

    // Inline errors / Toast Notifications
    const [error, setError] = useState(null);
    const [guestModalError, setGuestModalError] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });
    const [finalRateWarning, setFinalRateWarning] = useState(false);
        const [taxExemptByRoom, setTaxExemptByRoom] = useState({});
    const [finalRateByRoom, setFinalRateByRoom] = useState({});
    const [finalRateWarningByRoom, setFinalRateWarningByRoom] = useState({});
    const [isRoomBreakdownOpen, setIsRoomBreakdownOpen] = useState(true);

    // Fetch manual booking sources if 'Direct' is selected
    useEffect(() => {
        if (bookingSource === 'Direct' && open) {
            fetchManualSources();
        }
    }, [bookingSource, open]);

    const fetchManualSources = async () => {
        try {
            const data = await SettingsService.getBookingSources(tenantId, { type: 'Direct', status: 'Active' });
            const sources = Array.isArray(data) ? data : [];
            setManualSources(sources);
        } catch (error) {
            console.error('Failed to fetch manual sources:', error);
        }
    };

    const [debugFullData, setDebugFullData] = useState(null);

    // Fetch group data on edit
    useEffect(() => {
        if (open && editData) {
            setActiveStep(0);
            setIsPrefilling(true);
            const fetchGroupData = async () => {
                try {
                    const res = await ReservationService.getById(editData.id, tenantId);
                    const fullData = res.data || res;
                    setDebugFullData(fullData);

                    const detectedChannel = fullData.bookingChannel || fullData.channel || fullData.bookingSourceDetails?.type || (['Direct', 'Corporate', 'OTA'].includes(fullData.bookingSource) ? fullData.bookingSource : (fullData.corporateName || fullData.isCorporate ? 'Corporate' : 'Direct'));
                    setBookingSource(detectedChannel);
                    setSelectedManualSource(fullData.bookingSourceId || fullData.bookingSourceDetails?.id || '');
                    const corpName = fullData.corporateName || (detectedChannel === 'Corporate' ? (fullData.bookingSourceDetails?.name || fullData.bookingSource || '') : '');
                    setCorporateName(corpName);
                    setGroupName(fullData.groupName || '');

                    const organiser = fullData.guest || fullData.organiser || {};
                    setOrganiserName(organiser.name || '');
                    setGuestInputValue(organiser.name || '');
                    setOrganiserEmail(organiser.email || '');

                    // Parse phone code if present
                    const rawPhone = organiser.phone || '';
                    const pCode = organiser.phoneCode || '';
                    if (pCode) {
                        setOrganiserPhoneCode(pCode);
                    }
                    const phoneMatch = rawPhone.match(/^(\+\d{1,4})\s*(.+)$/);
                    if (phoneMatch) {
                        if (!pCode) setOrganiserPhoneCode(phoneMatch[1]);
                        setOrganiserPhone(phoneMatch[2].replace(/\D/g, ''));
                    } else {
                        const digitsOnly = rawPhone.replace(/\D/g, '');
                        setOrganiserPhone(digitsOnly);
                        if (!pCode && isUae) {
                            setOrganiserPhoneCode('+971');
                        }
                    }

                    setCheckIn(fullData.checkIn ? fullData.checkIn.split('T')[0] : '');
                    setCheckOut(fullData.checkOut ? fullData.checkOut.split('T')[0] : '');
                    const backendBillingMode = fullData.billingMode || fullData.payment?.billingMode || '';
                    const isIndividual = backendBillingMode === 'Individual' || backendBillingMode === 'Individual Billing' || backendBillingMode === 'individual';

                    if (isIndividual) {
                        setBillingMode('Individual Billing');
                    } else if (backendBillingMode === 'MasterFolio' || backendBillingMode === 'Master Bill' || backendBillingMode === 'master') {
                        setBillingMode('Master Bill');
                    } else {
                        setBillingMode('');
                    }

                    setPaymentMethod(fullData.payment?.paymentMethod || fullData.paymentMethod || '');
                    setAdvancePaid(fullData.billing?.advancePaid || fullData.payment?.advancePaid || fullData.advancePaid || fullData.advanceAmount || '');

                    const backendRoomsCount = (fullData.childReservations || fullData.rooms || []).length || 1;
                    const explicitOverrideVal = fullData.rateOverride?.value !== undefined ? Number(fullData.rateOverride.value) : undefined;
                    const entireStayRo = Array.isArray(fullData.rateOverrides) ? fullData.rateOverrides.find(ro => ro.overrideType === 'EntireStay') : null;
                    const roVal = entireStayRo ? Number(entireStayRo.overriddenRate !== undefined ? entireStayRo.overriddenRate : entireStayRo.value) : undefined;
                    const netTaxableVal = (fullData.billing?.netTaxableValue !== undefined && fullData.billing?.netTaxableValue !== null)
                        ? Number(fullData.billing.netTaxableValue)
                        : (fullData.billing?.netTaxableAmount !== undefined && fullData.billing?.netTaxableAmount !== null)
                        ? Number(fullData.billing.netTaxableAmount)
                        : (fullData.netTaxableValue !== undefined ? Number(fullData.netTaxableValue)
                        : (fullData.netTaxableAmount !== undefined ? Number(fullData.netTaxableAmount)
                        : (fullData.financials?.netTaxableAmount !== undefined ? Number(fullData.financials.netTaxableAmount) : undefined)));
                    const subtotalVal = Number(fullData.billing?.roomSubtotal || fullData.billing?.subtotal || fullData.financials?.roomSubtotal || 0);
                    const discountVal = Number(fullData.billing?.overrideOrDiscount || fullData.billing?.discountAmount || fullData.financials?.roomOverrideOrDiscount || fullData.discounts || 0);
                    const discountSubtractedVal = (subtotalVal > 0 && discountVal > 0) ? (subtotalVal - discountVal) : undefined;
                    const finalRateVal = (fullData.finalRate !== undefined && fullData.finalRate !== null && fullData.finalRate !== '')
                        ? Number(fullData.finalRate)
                        : undefined;

                    const globalFR = (explicitOverrideVal !== undefined && !isNaN(explicitOverrideVal)) ? explicitOverrideVal
                        : (roVal !== undefined && !isNaN(roVal) && roVal > 0) ? roVal
                        : (netTaxableVal !== undefined && !isNaN(netTaxableVal) && netTaxableVal > 0) ? netTaxableVal
                        : (discountSubtractedVal !== undefined && !isNaN(discountSubtractedVal) && discountSubtractedVal > 0) ? discountSubtractedVal
                        : (finalRateVal !== undefined && !isNaN(finalRateVal) && finalRateVal > 0) ? finalRateVal
                        : Number(fullData.billing?.finalAmount || 0);
                    const isExempt = fullData.taxExempt !== undefined ? fullData.taxExempt : (fullData.billing?.taxExempted || false);

                    let hasEntireStayOverride = false;
                    if (fullData.rooms && fullData.rooms.length > 0) {
                        hasEntireStayOverride = fullData.rooms.some(r => r.rateOverride && (r.rateOverride.type === 'FinalRateOverrdie' || r.rateOverride.type === 'FinalRateOverride') && r.rateOverride.value !== undefined);
                    }
                    if (!hasEntireStayOverride && fullData.rateOverrides && fullData.rateOverrides.length > 0) {
                        hasEntireStayOverride = fullData.rateOverrides.some(ro => ro.overrideType === 'EntireStay' && ro.reasonText && (ro.reasonText.includes('FinalRateOverride') || ro.reasonText.includes('FinalRateOverrdie')));
                    }
                    if (!hasEntireStayOverride && fullData.isFinalRateOverride) {
                        hasEntireStayOverride = true;
                    }
                    
                    // Isolated fallback for Master Bill Final Override where reasonText might be empty
                    if (!hasEntireStayOverride && !isIndividual && fullData.rateOverrides && fullData.rateOverrides.length > 0) {
                        const roomsHaveOverrides = (fullData.rooms || fullData.childReservations || []).some(r => {
                            const hasDaily = r.dailyRates && r.dailyRates.some(d => d.overrideRate !== undefined && d.overrideRate !== null && String(d.overrideRate) !== '');
                            const hasRoomOverride = r.overrides && r.overrides.length > 0;
                            const hasLegacyOverride = r.rateOverride && r.rateOverride.type !== 'FinalRateOverride' && r.rateOverride.type !== 'FinalRateOverrdie';
                            return hasDaily || hasRoomOverride || hasLegacyOverride;
                        });
                        
                        if (!roomsHaveOverrides) {
                            hasEntireStayOverride = fullData.rateOverrides.some(ro => ro.overrideType === 'EntireStay' && (!ro.reasonText || ro.reasonText === '{}' || ro.reasonText === '""'));
                        }
                    }

                    // Failsafe for Master Bill Final Override when backend drops rateOverrides array but records discount
                    if (!hasEntireStayOverride && !isIndividual) {
                        const roomsHaveOverrides = (fullData.rooms || fullData.childReservations || []).some(r => {
                            const hasDaily = r.dailyRates && r.dailyRates.some(d => d.overrideRate !== undefined && d.overrideRate !== null && String(d.overrideRate) !== '');
                            const hasRoomOverride = r.overrides && r.overrides.length > 0;
                            return hasDaily || hasRoomOverride;
                        });
                        
                        const rootDiscount = Number(fullData.billing?.overrideOrDiscount || fullData.discounts || fullData.billing?.discountAmount || 0);
                        if (!roomsHaveOverrides && rootDiscount > 0) {
                            hasEntireStayOverride = true;
                        }
                    }

                    if (isIndividual) {
                        const frObj = {};
                        const teObj = {};

                        const roomBreakdown = fullData.billing?.roomBreakdown || fullData.billing?.rooms || [];
                        const backendRoomsArr = fullData.childReservations || fullData.rooms || [];

                        for (let i = 0; i < backendRoomsCount; i++) {
                            const rEntity = backendRoomsArr[i] || {};
                            const rb = roomBreakdown.find(r => (r.roomId && (r.roomId === rEntity.roomId || r.roomId === rEntity.id)) || (r.roomIndex !== undefined && r.roomIndex === i + 1)) || roomBreakdown[i] || {};
                            
                            const isExplicitFinalOverride = (rEntity.rateOverride && (rEntity.rateOverride.type === 'FinalRateOverride' || rEntity.rateOverride.type === 'FinalRateOverrdie') && rEntity.rateOverride.value !== undefined)
                                || (rEntity.overrides && rEntity.overrides.some(ro => ro.overrideType === 'EntireStay' && ro.reasonText && (ro.reasonText.includes('FinalRateOverride') || ro.reasonText.includes('FinalRateOverrdie'))));

                            const explicitRoomOverride = (rEntity.rateOverride && (rEntity.rateOverride.type === 'FinalRateOverride' || rEntity.rateOverride.type === 'FinalRateOverrdie') && rEntity.rateOverride.value !== undefined)
                                ? Number(rEntity.rateOverride.value)
                                : undefined;

                            const rbNet = (rb.netTaxableValue !== undefined && rb.netTaxableValue !== null)
                                ? Number(rb.netTaxableValue)
                                : (rb.netTaxableAmount !== undefined && rb.netTaxableAmount !== null)
                                ? Number(rb.netTaxableAmount)
                                : undefined;

                            const rbDiscount = Number(rb.discountApplied || rb.discount || 0);
                            const rbRate = Number(rb.roomRate || rb.rateAmount || 0);
                            const derivedRbNet = (rbRate > 0 && rbDiscount > 0) ? (rbRate - rbDiscount) : undefined;

                            const roomFinalRate = (explicitRoomOverride !== undefined && !isNaN(explicitRoomOverride)) ? explicitRoomOverride
                                : (rbNet !== undefined && !isNaN(rbNet) && rbNet > 0) ? rbNet
                                : (derivedRbNet !== undefined && !isNaN(derivedRbNet) && derivedRbNet > 0) ? derivedRbNet
                                : (rEntity.netTaxableValue !== undefined && !isNaN(Number(rEntity.netTaxableValue))) ? Number(rEntity.netTaxableValue)
                                : 0;

                            frObj[i] = (isExplicitFinalOverride && roomFinalRate > 0) ? roomFinalRate : ''; 
                            const backendTaxExempt = rEntity.taxExempt !== undefined ? rEntity.taxExempt : (rb.taxExempt === true);
                            teObj[i] = backendTaxExempt || isExempt;
                        }
                        setFinalRateByRoom(frObj);
                        setTaxExemptByRoom(teObj);

                        setReservationFinalRate('');
                        setTaxExempt(false);
                        const anyRoomHasFinalOverride = Object.values(frObj).some(val => val !== '');
                        setOverrideMode(anyRoomHasFinalOverride ? 'Final' : 'Daily');
                    } else {
                        setBillingMode('Master Bill');
                        setOverrideMode(hasEntireStayOverride ? 'Final' : 'Daily');
                        setReservationFinalRate((hasEntireStayOverride && globalFR > 0) ? globalFR : '');
                        
                        setTaxExempt(isExempt);
                    }
                    setDiscounts(fullData.billing?.overrideOrDiscount || fullData.billing?.discountAmount || fullData.discounts || '');

                    let inventoryRooms = [];
                    try {
                        const invRes = await RoomService.getAll({ noPagination: true }, tenantId);
                        inventoryRooms = Array.isArray(invRes) ? invRes : (invRes.data || invRes.rooms || []);
                    } catch (err) {
                        console.error("Failed to load full inventory for edit map", err);
                    }

                    const backendRooms = fullData.childReservations || fullData.rooms || [];
                    if (backendRooms && backendRooms.length > 0) {
                        const decoratedRooms = [];

                        const prefilledRooms = await Promise.all(backendRooms.map(async r => {
                            let actualRoom = r.room || inventoryRooms.find(inv => inv.id === r.roomId);

                            // Fallback: fetch room by ID if not found in inventory list
                            if (!actualRoom && r.roomId) {
                                try {
                                    const fetched = await RoomService.getById(r.roomId);
                                    actualRoom = fetched.data || fetched;
                                } catch (e) {
                                    console.error("Failed to fetch prefilled room:", r.roomId);
                                }
                            }

                            if (actualRoom && !decoratedRooms.find(dr => (dr.id || dr._id) === (actualRoom.id || actualRoom._id))) {
                                decoratedRooms.push({ ...actualRoom, _isPrefilled: true });
                            }
                            return { ...r, _actualRoom: actualRoom };
                        }));

                        setRooms(prev => {
                            const merged = [...prev];
                            decoratedRooms.forEach(pr => {
                                if (!merged.find(m => (m.id || m._id) === (pr.id || pr._id))) merged.push(pr);
                            });
                            return merged;
                        });

                        const mappedRoomsArr = await Promise.all(prefilledRooms.map(async r => {
                            const actualRoom = r._actualRoom;
                            const roomTypeId = actualRoom?.roomTypeId || actualRoom?.roomType?.id || r.roomType?.id;
                            let ratePlanList = [];
                            if (roomTypeId) {
                                try {
                                    const payload = {
                                        roomTypeId,
                                        startDate: fullData.checkIn ? fullData.checkIn.split('T')[0] : '',
                                        endDate: fullData.checkOut ? fullData.checkOut.split('T')[0] : ''
                                    };
                                    if (fullData.bookingSource) {
                                        payload.Channel = fullData.bookingSource === 'Corporate' ? 'Corp' : 'Direct';
                                    }
                                    const ratesResult = await RateService.getRatePlans(tenantId, payload);
                                    const ratesList = Array.isArray(ratesResult) ? ratesResult : (ratesResult.data || ratesResult.ratePlans || []);
                                    ratePlanList = ratesList.filter(plan => plan.status === 'Active');
                                    setRatePlansByRoomType(prev => ({ ...prev, [roomTypeId]: ratePlanList }));
                                } catch (error) {
                                    console.error("Failed to load rate plans for prefilled group room", error);
                                }
                            }

                            const roomGuests = r.guestDetails || r.guests || [];
                            const adultsTarget = r.adultsCount || 1;
                            const kidsTarget = r.kidsCount || 0;

                            const mappedGuests = [];
                            const mappedKids = [];

                            roomGuests.forEach(gInfo => {
                                const g = gInfo.guest || gInfo;
                                if (g.guestType === 'Child' || g.age !== undefined) {
                                    mappedKids.push({
                                        firstName: (g.name || '').trim().split(' ')[0] || g.firstName || '',
                                        lastName: (g.name || '').trim().split(' ').slice(1).join(' ') || g.lastName || '',
                                        age: g.childAge !== undefined ? g.childAge : (g.age || ''),
                                        dob: g.birthDate || g.dob || g.dateOfBirth || '',
                                        idProofPreviewUrl: g.idDocumentUrl || null
                                    });
                                } else {
                                    const nameParts = (g.name || '').trim().split(' ');
                                    const gRawPhone = g.phone || g.phoneNumber || '';
                                    let gPCode = g.phoneCode || '';
                                    let gPhoneFinal = '';
                                    const gPhoneMatch = gRawPhone.match(/^(\+\d{1,4})\s*(.+)$/);
                                    if (gPhoneMatch) {
                                        if (!gPCode) gPCode = gPhoneMatch[1];
                                        gPhoneFinal = gPhoneMatch[2].replace(/\D/g, '');
                                    } else {
                                        const digitsOnly = gRawPhone.replace(/\D/g, '');
                                        gPhoneFinal = digitsOnly.length > 10 && (gPCode === '+91' || (!gPCode && !isUae)) ? digitsOnly.slice(-10) : digitsOnly;
                                    }
                                    if (!gPCode) {
                                        gPCode = isUae ? '+971' : '+91';
                                    }
                                    mappedGuests.push({
                                        firstName: nameParts[0] || '',
                                        lastName: nameParts.slice(1).join(' ') || '',
                                        email: g.email || '',
                                        phoneCode: gPCode,
                                        phone: gPhoneFinal,
                                        address: g.address || '',
                                        zip: g.zip || '',
                                        country: g.country || '',
                                        state: g.state || '',
                                        city: g.city || '',
                                        idProofFile: null,
                                        idProofPreviewUrl: g.idDocumentUrl || null,
                                        idNumber: g.idProof || '',
                                        idType: g.idType || '',
                                        nationality: g.nationality || ''
                                    });
                                }
                            });

                            if (mappedGuests.length === 0) mappedGuests.push({ ...initialGuestState });

                            const targetPlan = ratePlanList.find(p => p.id === r.ratePlanId) || r.ratePlan;
                            let computedRate = '';
                            if (targetPlan) {
                                computedRate = calculateFinalRoomPrice({
                                    isBaseRate: targetPlan.type === 'Base',
                                    isDefault: targetPlan.isDefault,
                                    roomRateAmount: actualRoom?.baseRate,
                                    roomTypeAmount: actualRoom?.roomType?.baseRate,
                                    customRatePlanAmount: targetPlan.rate || targetPlan.baseRate || 0
                                });
                            } else if (r.rateAmount !== undefined && r.rateAmount !== null && r.rateAmount !== '') {
                                computedRate = r.rateAmount;
                            } else if (r.dailyRates && r.dailyRates.length > 0 && r.dailyRates[0].baseRate) {
                                computedRate = r.dailyRates[0].baseRate;
                            } else {
                                computedRate = r.price || actualRoom?.baseRate || '';
                            }

                            return {
                                roomId: r.roomId || actualRoom?.id || actualRoom?._id || '',
                                ratePlanId: r.ratePlanId || '',
                                rateAmount: computedRate,
                                adultsCount: adultsTarget,
                                kidsCount: kidsTarget,
                                extraBed: Boolean(r.extraBed || r.hasExtraBed || r.extra_bed || fullData.extraBed || fullData.hasExtraBed || fullData.extra_bed),
                                isExisting: true,
                                guests: mappedGuests,
                                kids: mappedKids
                            };
                        }));
                        setRoomsArr(mappedRoomsArr);

                        // --- Bulk/Rate Overrides Prefill Logic ---
                        const initialDailyRates = {};
                        const checkInDate = new Date(fullData.checkIn ? fullData.checkIn.split('T')[0] : '');
                        const checkOutDate = new Date(fullData.checkOut ? fullData.checkOut.split('T')[0] : '');
                        const diffDays = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24)) || 1;

                        const initialAppliedBulk = {};
                        const initialOverrideReasons = {};

                        mappedRoomsArr.forEach((r, idx) => {
                            const days = [];
                            const tempDate = new Date(checkInDate);
                            const roomEntity = backendRooms[idx];
                            
                            let backendDailyRates = roomEntity.dailyRates || [];
                            if (backendDailyRates.length === 0 && fullData.rateOverrides) {
                                const specificDatesOverrides = fullData.rateOverrides.filter(ro => 
                                    ro.overrideType === 'SpecificDates' && ro.reservationRoomId === roomEntity.id
                                );
                                backendDailyRates = specificDatesOverrides.map(ro => ({
                                    date: ro.startDate,
                                    baseRate: ro.originalRate,
                                    overrideRate: ro.overriddenRate,
                                    reason: ro.reasonText || ro.reasonCategory
                                }));
                            }

                            for (let i = 0; i < diffDays; i++) {
                                const dateStr = tempDate.toISOString().split('T')[0];
                                const backendDay = backendDailyRates.find(d => d.date.split('T')[0] === dateStr);

                                const baseRate = backendDay ? Number(backendDay.baseRate) : Number(r.rateAmount || 0);
                                let overrideRate = '';

                                if (backendDay && backendDay.overrideRate !== undefined && backendDay.overrideRate !== null) {
                                    overrideRate = Number(backendDay.overrideRate);
                                    if (overrideRate === baseRate) {
                                        overrideRate = '';
                                    }
                                }

                                days.push({
                                    date: dateStr,
                                    baseRate: baseRate,
                                    overrideRate: overrideRate !== '' ? Number(overrideRate) : ''
                                });
                                tempDate.setDate(tempDate.getDate() + 1);
                            }
                            initialDailyRates[idx] = days;
                            
                            if (roomEntity.reason && roomEntity.reason !== 'Frontend daily rate entry') {
                                initialOverrideReasons[idx] = roomEntity.reason;
                            } else if (roomEntity.rateOverride && roomEntity.rateOverride.reason && roomEntity.rateOverride.reason !== 'Frontend daily rate entry') {
                                initialOverrideReasons[idx] = roomEntity.rateOverride.reason;
                            } else if (backendDailyRates.length > 0) {
                                const appliedDay = backendDailyRates.find(d => d.overrideRate !== '' && d.overrideRate !== undefined);
                                if (appliedDay && appliedDay.reason && appliedDay.reason !== 'Frontend daily rate entry') {
                                    initialOverrideReasons[idx] = appliedDay.reason;
                                }
                            }
                            
                            if (roomEntity.rateOverride && !hasEntireStayOverride && roomEntity.rateOverride.value !== undefined) { 
                                if (roomEntity.rateOverride.type === 'Percentage' || roomEntity.rateOverride.type === 'Fixed') {
                                    initialAppliedBulk[idx] = {
                                        type: roomEntity.rateOverride.type,
                                        discount: roomEntity.rateOverride.value
                                    };
                                }
                            }
                        });

                        setDailyRatesByRoom(initialDailyRates);
                        setOriginalDailyRatesByRoom(initialDailyRates);
                        setAppliedBulkByRoom(initialAppliedBulk);
                        setOverrideReasons(initialOverrideReasons);

                        // --- Prefill Bulk Rate Override UI Fields ---
                        const firstAppliedIdx = Object.keys(initialAppliedBulk)[0];
                        if (firstAppliedIdx) {
                            setEnableBulkRateOverride(true);
                            setBulkDiscountType(initialAppliedBulk[firstAppliedIdx].type);
                            setBulkDiscount(initialAppliedBulk[firstAppliedIdx].discount);

                            // Check if the same discount applies to all rooms to prefill the scope appropriately
                            const allRoomsHaveSame = mappedRoomsArr.every((_, idx) =>
                                initialAppliedBulk[idx] &&
                                initialAppliedBulk[idx].discount === initialAppliedBulk[firstAppliedIdx].discount &&
                                initialAppliedBulk[idx].type === initialAppliedBulk[firstAppliedIdx].type
                            );

                            if (allRoomsHaveSame && mappedRoomsArr.length > 1) {
                                setBulkApplyScope('Entire Group');
                            }
                        }

                        // Fetch rate plans for these rooms
                        backendRooms.forEach(async (r) => {
                            const roomTypeId = r.roomTypeId || r.room?.roomTypeId || r.room?.roomType?.id || r.roomType?.id;
                            if (roomTypeId) {
                                try {
                                    const payload = {
                                        roomTypeId,
                                        startDate: fullData.checkIn ? fullData.checkIn.split('T')[0] : '',
                                        endDate: fullData.checkOut ? fullData.checkOut.split('T')[0] : ''
                                    };
                                    if (fullData.bookingSource) {
                                        payload.Channel = fullData.bookingSource === 'Corporate' ? 'Corp' : 'Direct';
                                    }
                                    const ratesResult = await RateService.getRatePlans(tenantId, payload);
                                    const ratesList = Array.isArray(ratesResult) ? ratesResult : (ratesResult.data || ratesResult.ratePlans || []);
                                    const activeRatesList = ratesList.filter(plan => plan.status === 'Active');
                                    setRatePlansByRoomType(prev => ({ ...prev, [roomTypeId]: activeRatesList }));
                                } catch (error) {
                                    console.error("Failed to load rate plans for prefilled group room", error);
                                }
                            }
                        });
                    }
                } catch (error) {
                    console.error("Failed to fetch group edit data:", error);
                    setToast({ open: true, message: 'Failed to load group details', severity: 'error' });
                } finally {
                    setIsPrefilling(false);
                }
            };
            fetchGroupData();
        }
    }, [open, editData, tenantId]);

    // Fetch taxes on mount/open
    useEffect(() => {
        if (!open) return;

        const fetchTaxes = async () => {
            try {
                const taxesResult = await SettingsService.getTaxes(tenantId, isUae ? {} : { serviceType: 'room' });
                const taxesList = Array.isArray(taxesResult) ? taxesResult : (taxesResult.data || taxesResult.taxes || []);
                setActiveTaxes(taxesList.filter(t => t.status === 'Active'));
            } catch (err) {
                console.error("Failed to load taxes:", err);
            }
        };

        fetchTaxes();
    }, [open, tenantId, isUae]);

    // Fetch payment methods on mount/open
    useEffect(() => {
        if (!open) return;

        const fetchPaymentMethods = async () => {
            setIsLoadingPaymentMethods(true);
            try {
                const res = await SettingsService.getPaymentMethods(tenantId, { noPagination: true });
                const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
                const activeList = list.filter(m => m.enabled === undefined || m.enabled === true || m.status === 'Active');
                setPaymentMethods(activeList);
            } catch (err) {
                console.error("Failed to load payment methods:", err);
                setPaymentMethods([]);
            } finally {
                setIsLoadingPaymentMethods(false);
            }
        };

        fetchPaymentMethods();
    }, [open, tenantId]);

    // Fetch available rooms ONLY when checkIn and checkOut dates are selected
    useEffect(() => {
        if (!open || !checkIn || !checkOut) {
            setRooms([]);
            return;
        }

        const fetchRooms = async () => {
            setIsLoadingRooms(true);
            try {
                const roomsResult = await RoomService.getAll({
                    noPagination: true,
                    checkIn,
                    checkOut
                }, tenantId);

                const roomsList = Array.isArray(roomsResult) ? roomsResult : (roomsResult.data || roomsResult.rooms || []);
                const availableRooms = roomsList.filter(r => r.status === 'Available' || r.status === 'Occupied');

                setRooms(prevRooms => {
                    const merged = [...availableRooms];
                    // Preserve any rooms injected from edit data
                    prevRooms.forEach(pr => {
                        if (pr._isPrefilled && !merged.find(m => (m.id || m._id) === (pr.id || pr._id))) {
                            merged.push(pr);
                        }
                    });
                    return merged;
                });
            } catch (err) {
                console.error("Failed to load room inventory:", err);
                setToast({ open: true, message: "Failed to load room inventory", severity: 'error' });
            } finally {
                setIsLoadingRooms(false);
            }
        };

        fetchRooms();
    }, [open, checkIn, checkOut, tenantId]);

    // Guest Autocomplete Search Effect
    useEffect(() => {
        let active = true;
        if (guestInputValue === '') {
            setGuestOptions([]);
            return undefined;
        }

        const timer = setTimeout(async () => {
            setGuestSearchLoading(true);
            try {
                const res = await GuestService.searchGuests({ search: guestInputValue, noPagination: true }, tenantId);
                const list = Array.isArray(res) ? res : (res.data || res.guests || []);
                if (active) setGuestOptions(list);
            } catch (e) {
                console.error("Guest search error", e);
            } finally {
                if (active) setGuestSearchLoading(false);
            }
        }, 400);

        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [guestInputValue, tenantId]);

    const resetForm = () => {
        setBookingSource('');
        setSelectedManualSource('');
        setCorporateName('');
        setShowCorporateMatchModal(false);
        setExistingCorporateMatch(null);
        setGroupName('');
        setOrganiserName('');
        setOrganiserEmail('');
        setOrganiserPhone('');
        setOrganiserPhoneCode(isUae ? '+971' : '+91');
        setGuestInputValue('');
        setCheckIn('');
        setCheckOut('');
        setRoomsArr([{ roomId: '', ratePlanId: '', finalRate: '', rateAmount: '', adultsCount: 1, kidsCount: 0, extraBed: false, guests: [{ ...initialGuestState }], kids: [] }]);
        setTotalAmount('0.00');
        setReservationFinalRate('');
        setDiscounts('');
        setTotalAmount('0.00');
        setRoomSubTotal('0.00');
        setError(null);
        setToast({ open: false, message: '', severity: 'error' });
        setSelectedRateRoomIndex(0);
        setDailyRatesByRoom({});
        setEnableBulkRateOverride(false);
        setBulkDiscountType('Percentage');
        setBulkDiscount('');
        setBulkApplyScope('This Room Only');
        setPaymentMethod('');
        setAdvancePaid('');
        setBillingMode('Master Bill');
        setTaxExempt(false);
        setAppliedBulkByRoom({});
        setOverrideReasons({});
        setFinalRateByRoom({});
        setTaxExemptByRoom({});
        setOverrideMode('Daily');
        setActiveStep(0);
    };

    // Reset form when modal opens
    useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open]);

    // Sync Bulk Override UI state when switching room tabs
    useEffect(() => {
        const existingBulk = appliedBulkByRoom[selectedRateRoomIndex];
        if (existingBulk) {
            setEnableBulkRateOverride(true);
            setBulkDiscount(existingBulk.discount || existingBulk.value || '');
            setBulkDiscountType(existingBulk.type || 'Percentage');
        } else {
            setEnableBulkRateOverride(false);
            setBulkDiscount('');
            setBulkDiscountType('Percentage');
        }
    }, [selectedRateRoomIndex, appliedBulkByRoom]);

    // Reset room selections when dates or booking source change to ensure rates are accurate
    useEffect(() => {
        if (open && !editData && (checkIn || checkOut || bookingSource)) {
            setRoomsArr(prev => prev.map(r => ({ ...r, roomId: '', ratePlanId: '', rateAmount: '', finalRate: '' })));
            setRatePlansByRoomType({});
            setReservationFinalRate('');
            setDailyRatesByRoom({});
        }
    }, [checkIn, checkOut, bookingSource, open, editData]);

    // Generate daily rate rows whenever dates or room rates change
    useEffect(() => {
        if (!checkIn || !checkOut) { setDailyRatesByRoom({}); return; }

        const days = [];
        const cur = new Date(checkIn);
        const end = new Date(checkOut);
        while (cur < end) {
            days.push(cur.toISOString().split('T')[0]);
            cur.setDate(cur.getDate() + 1);
        }

        setDailyRatesByRoom(prev => {
            const newRates = {};
            roomsArr.forEach((r, idx) => {
                const baseRate = Number(r.rateAmount || 0);
                const currentRoomRates = prev[idx] || [];
                newRates[idx] = days.map(dateStr => {
                    const existing = currentRoomRates.find(d => d.date === dateStr);
                    return { date: dateStr, baseRate, overrideRate: existing?.overrideRate ?? '' };
                });
            });
            return newRates;
        });
    }, [checkIn, checkOut, roomsArr]);

    // Auto-calculate Total Amount
    useEffect(() => {
        let subTotal = 0;
        roomsArr.forEach((r, idx) => {
            const days = dailyRatesByRoom[idx] || [];
            subTotal += days.reduce((s, d) => s + Number(overrideMode === 'Daily' && d.overrideRate !== '' ? d.overrideRate : d.baseRate), 0);
        });

        setRoomSubTotal(subTotal.toFixed(2));

        const targetRooms = roomsArr.map((r, i) => r.roomId ? i : -1).filter(i => i !== -1);
        const computedTax = calculateDynamicTax(activeTaxes, targetRooms, dailyRatesByRoom, taxExempt, overrideMode, roomsArr, isUae, hotel);

        setTaxTotal(computedTax.toFixed(2));

        const baseTotal = subTotal + 0 + computedTax;
        let computedDiscount = 0;

        if (reservationFinalRate && !isNaN(reservationFinalRate)) {
            computedDiscount = baseTotal - Number(reservationFinalRate);
        }

        setDiscounts(computedDiscount.toFixed(2));
        setTotalAmount(Math.max(0, baseTotal).toFixed(2));
    }, [checkIn, checkOut, roomsArr, dailyRatesByRoom, ratePlansByRoomType, activeTaxes, reservationFinalRate, taxExempt, isUae]);

    // Auto-clear errors when user modifies inputs
    useEffect(() => {
        if (error) setError(null);
    }, [bookingSource, groupName, organiserName, organiserEmail, organiserPhone, checkIn, checkOut, roomsArr, reservationFinalRate]);

    // Dynamically cap final rate override if base rate changes
    useEffect(() => {
        if (reservationFinalRate === '') {
            setFinalRateWarning(false);
            return;
        }

        // Prevent premature capping during intermediate renders when dailyRatesByRoom is not fully populated
        const isDailyRatesLoaded = roomsArr.length > 0 && roomsArr.every((r, idx) => dailyRatesByRoom[idx] && dailyRatesByRoom[idx].length > 0);
        const hasMissingRates = roomsArr.some(r => r.rateAmount === '' || r.rateAmount === undefined || r.rateAmount === null);
        if (!isDailyRatesLoaded || hasMissingRates) return;

        const totalBaseSubtotal = roomsArr.reduce((sum, r, idx) => {
            const days = dailyRatesByRoom[idx] || [];
            return sum + days.reduce((s, d) => s + Number(d.baseRate || 0), 0);
        }, 0);

        if (Number(reservationFinalRate) > totalBaseSubtotal) {
            console.warn(`Squashing final rate from ${reservationFinalRate} to ${totalBaseSubtotal}`);
            setReservationFinalRate(totalBaseSubtotal.toString());
            setFinalRateWarning(true);
        } else {
            setFinalRateWarning(false);
        }
    }, [roomsArr, dailyRatesByRoom, checkIn, checkOut, reservationFinalRate]);

    // Validation
    const validateForm = (step) => {
        if (!checkIn || !checkOut || !groupName || !organiserName || !organiserEmail || !organiserPhone) {
            return "All basic group and organiser fields are required.";
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(organiserEmail)) {
            return "Please enter a valid email address.";
        }
        if (bookingSource === 'Corporate' && !corporateName && !selectedManualSource) {
            return "For Corporate bookings, please provide a Corporate Name or select a Source.";
        }
        if (bookingSource !== 'Corporate' && !selectedManualSource) {
            return "Booking Channel is strictly required.";
        }
        const phoneErr = validatePhoneByCode(organiserPhone, organiserPhoneCode);
        if (phoneErr) {
            return phoneErr.replace('Please enter a primary phone number.', 'Organiser phone number is required.').replace('phone number', 'organiser phone number');
        }
        if (new Date(checkOut) <= new Date(checkIn)) {
            return "Check-out date must be after Check-in date.";
        }

        if (step === 1) {
            if (overrideMode === 'Final') {
                if (billingMode === 'Master Bill' && reservationFinalRate !== '') {
                    const totalBaseSubtotal = roomsArr.reduce((sum, r, idx) => {
                        const days = dailyRatesByRoom[idx] || [];
                        return sum + days.reduce((s, d) => s + Number(d.baseRate || 0), 0);
                    }, 0);
                    if (Number(reservationFinalRate) > totalBaseSubtotal) {
                        return "Final Rate Override cannot exceed the Total Base Rate.";
                    }
                } else if (billingMode === 'Individual Billing') {
                    for (let i = 0; i < roomsArr.length; i++) {
                        if (!roomsArr[i].roomId) continue;
                        const rDays = dailyRatesByRoom[i] || [];
                        const rBase = rDays.reduce((s, d) => s + Number(d.baseRate || 0), 0);
                        if (finalRateByRoom[i] !== undefined && finalRateByRoom[i] !== '' && Number(finalRateByRoom[i]) > rBase) {
                            return `Final Rate Override for Room ${i + 1} cannot exceed the Base Rate.`;
                        }
                    }
                }
            }
            if (!billingMode) {
                return "Billing Mode is strictly required.";
            }
        }
        const assignedRoomIds = new Set();
        if (roomsArr.length === 0) return "Please add at least one room to this reservation.";
        for (let blockIdx = 0; blockIdx < roomsArr.length; blockIdx++) {
            const r = roomsArr[blockIdx];
            const roomInfo = rooms.find(room => (room.id || room._id) === r.roomId);
            const roomLabel = roomInfo ? `Room ${roomInfo.roomNumber} (${roomInfo.roomType?.name || roomInfo.type || 'Standard'})` : `Block ${blockIdx + 1}`;
            
            if (!r.roomId || !r.ratePlanId) return `Please assign a specific Room and Rate Plan for ${roomLabel}.`;
            if (r.adultsCount < 1) return `${roomLabel} must have at least one adult guest.`;
            if (assignedRoomIds.has(r.roomId)) {
                return `Room ${roomLabel} has already been selected. Please choose a different room.`;
            }
            assignedRoomIds.add(r.roomId);
            // First and last name validation removed as requested
        }
        return null;
    };

    useEffect(() => {
        if (isPrefilling) return;
        const selectedRooms = roomsArr.filter(r => r.roomId).length;
        if (selectedRooms <= 1 && billingMode === 'Individual Billing') {
            setBillingMode('Master Bill');
        }
    }, [roomsArr, billingMode, isPrefilling]);

    const handleGuestModalClose = () => {
        if (!roomsArr[activeRoomIndexForGuestModal]) {
            setShowGuestModal(false);
            return;
        }
        const guests = roomsArr[activeRoomIndexForGuestModal].guests;
        for (let i = 0; i < guests.length; i++) {
            const g = guests[i];
            if (g.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(g.email)) {
                setGuestModalError(`Guest ${i + 1}: Please enter a valid email address.`);
                setActiveGuestTab(i);
                return;
            }
            if (g.phone) {
                const cleanPhone = g.phone.replace(/\D/g, '');
                const targetCode = g.phoneCode || organiserPhoneCode || (isUae ? '+971' : '+91');
                const minLen = getMinPhoneLength(targetCode, isUae);
                if (cleanPhone.length < minLen || cleanPhone.length > 15) {
                    setGuestModalError(`Guest ${i + 1}: Please enter a valid phone number (at least ${minLen} digits).`);
                    setActiveGuestTab(i);
                    return;
                }
            }
        }
        const kids = roomsArr[activeRoomIndexForGuestModal].kids || [];
        for (let i = 0; i < kids.length; i++) {
            const k = kids[i];
            if (k.age && Number(k.age) > 12) {
                setGuestModalError(`Child ${i + 1}: Age must be 12 or under.`);
                setActiveGuestTab(`kid-${i}`);
                return;
            }
        }
        setGuestModalError(null);
        setShowGuestModal(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (activeStep === 0) {
            const step0Error = validateForm(0);
            if (step0Error) {
                setToast({ open: true, message: step0Error, severity: 'warning' });
            } else {
                setActiveStep(1);
            }
            return;
        }

        const validationError = validateForm(1);
        if (validationError) {
            setError(validationError);
            setToast({ open: true, message: validationError, severity: 'warning' });
            return;
        }

        setIsSubmitting(true);

        if (bookingSource === 'Corporate' && corporateName) {
            try {
                const corporateSources = await SettingsService.getBookingSources(tenantId, { type: 'Corporate' });
                const existing = (Array.isArray(corporateSources) ? corporateSources : []).find(
                    s => s.name.toLowerCase() === corporateName.trim().toLowerCase()
                );

                if (existing) {
                    const currentSourceId = editData?.bookingSourceId || editData?.bookingSourceDetails?.id;
                    if (!editData || existing.id !== currentSourceId) {
                        setExistingCorporateMatch(existing);
                        setShowCorporateMatchModal(true);
                        setIsSubmitting(false);
                        return; // Halt execution and intercept
                    }
                }
            } catch (err) {
                console.error("Failed to check corporate sources", err);
            }
        }

        executeSubmission(corporateName.trim());
    };

    const executeSubmission = async (finalCorporateName, forceNewProfile = false, linkExistingId = null) => {
        setIsSubmitting(true);
        try {
            // Pre-upload all group guest IDs
            const processedRooms = await Promise.all(roomsArr.map(async (r) => {
                const processedGuests = await Promise.all(r.guests.map(async (g) => {
                    let uploadedIdUrl = undefined;
                    if (g.idProofFile) {
                        try {
                            const uploadRes = await MediaService.uploadFile(g.idProofFile);
                            uploadedIdUrl = uploadRes?.url || uploadRes?.publicUrl || uploadRes;
                        } catch (e) {
                            console.error("Guest ID upload failed", e);
                            throw new Error(`Failed to upload ID document for guest ${g.name || 'Unnamed'}. Please try again.`);
                        }
                    }
                    return { ...g, uploadedIdUrl };
                }));

                const processedKids = await Promise.all((r.kids || []).map(async (k) => {
                    let uploadedIdUrl = undefined;
                    if (k.idProofFile) {
                        try {
                            const uploadRes = await MediaService.uploadFile(k.idProofFile);
                            uploadedIdUrl = uploadRes?.url || uploadRes?.publicUrl || uploadRes;
                        } catch (e) {
                            console.error("Child ID upload failed", e);
                            throw new Error(`Failed to upload ID document for child. Please try again.`);
                        }
                    }
                    return { ...k, uploadedIdUrl };
                }));

                return { ...r, processedGuests, processedKids };
            }));

            // --- Robust Price Recalculation ---
            let secureSubTotal = 0;
            const diffDays = Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)) || 1;

            roomsArr.forEach((r, idx) => {
                if (dailyRatesByRoom[idx] && dailyRatesByRoom[idx].length > 0) {
                    const days = dailyRatesByRoom[idx];
                    let roomTotal = 0;
                    for (let i = 0; i < diffDays; i++) {
                        if (i < days.length) {
                            roomTotal += Number(overrideMode === 'Daily' && days[i].overrideRate !== '' ? days[i].overrideRate : days[i].baseRate);
                        } else {
                            roomTotal += Number(r.rateAmount || 0);
                        }
                    }
                    secureSubTotal += roomTotal;
                } else {
                    secureSubTotal += Number(r.rateAmount || 0) * diffDays;
                }
            });

            let finalGlobalExempt = roomsArr.length > 0;
            let sumSubtotal = 0;
            let sumTax = 0;
            let sumPreDiscountTax = 0;
            let sumDiscount = 0;
            let sumFinal = 0;



            const targetRoomsIndices = roomsArr.map((r, i) => r.roomId ? i : -1).filter(i => i !== -1);
            const totalPreDiscountBaseAll = targetRoomsIndices.reduce((sum, i) => sum + (dailyRatesByRoom[i] || []).reduce((s, d) => s + Number(d.baseRate || 0), 0), 0);

            const masterRoomOverrides = {};
            if (overrideMode === 'Final' && billingMode === 'Master Bill' && reservationFinalRate !== '') {
                const totalOverrideVal = Number(reservationFinalRate);
                let allocatedSoFar = 0;
                targetRoomsIndices.forEach((i, tIdx) => {
                    const roomBase = (dailyRatesByRoom[i] || []).reduce((s, d) => s + Number(d.baseRate || 0), 0);
                    if (tIdx === targetRoomsIndices.length - 1) {
                        masterRoomOverrides[i] = Math.max(0, Math.round((totalOverrideVal - allocatedSoFar) * 100) / 100);
                    } else {
                        const prop = totalPreDiscountBaseAll > 0 ? (roomBase / totalPreDiscountBaseAll) : (1 / (targetRoomsIndices.length || 1));
                        const val = Math.round(totalOverrideVal * prop * 100) / 100;
                        masterRoomOverrides[i] = val;
                        allocatedSoFar += val;
                    }
                });
            }

            let totalServiceCharge = 0;
            let totalMunicipalityFee = 0;
            let totalVat = 0;
            let totalTourismDirham = 0;
            let primaryTourismDirhamRate = 15;

            const computedRoomsBilling = roomsArr.map((r, idx) => {
                const days = dailyRatesByRoom[idx] || [];
                const rExempt = taxExemptByRoom[idx] || taxExempt || false;
                if (!rExempt) finalGlobalExempt = false;

                const diffDays = Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)) || 1;
                const rPreDiscountBase = days.reduce((s, d) => s + Number(d.baseRate), 0) + (diffDays > days.length ? (diffDays - days.length) * Number(r.rateAmount || 0) : 0);
                
                let rNet = rPreDiscountBase;
                let rDiscount = 0;
                let rTax = calculateDynamicTax(activeTaxes, [idx], dailyRatesByRoom, rExempt, 'Daily', roomsArr, isUae, hotel);

                if (overrideMode === 'Daily') {
                    rNet = 0;
                    for (let i = 0; i < diffDays; i++) {
                        if (i < days.length) rNet += Number(overrideMode === 'Daily' && days[i].overrideRate !== '' ? days[i].overrideRate : days[i].baseRate);
                        else rNet += Number(r.rateAmount || 0);
                    }
                    rTax = calculateDynamicTax(activeTaxes, [idx], dailyRatesByRoom, rExempt, 'Daily', roomsArr, isUae, hotel);
                    rDiscount = Math.max(0, rPreDiscountBase - rNet);
                } else if (overrideMode === 'Final') {
                    if (billingMode === 'Individual Billing') {
                        if (finalRateByRoom[idx] !== undefined && finalRateByRoom[idx] !== '') {
                            const overrideVal = Math.min(rPreDiscountBase, Number(finalRateByRoom[idx]));
                            rNet = overrideVal;
                            rDiscount = Math.max(0, rPreDiscountBase - rNet);
                            rTax = calculateRoomTaxFromOverride(activeTaxes, idx, rNet, dailyRatesByRoom, rExempt, roomsArr, isUae, hotel);
                        } else {
                            rNet = rPreDiscountBase;
                            rDiscount = 0;
                            rTax = calculateDynamicTax(activeTaxes, [idx], dailyRatesByRoom, rExempt, 'Daily', roomsArr, isUae, hotel);
                        }
                    } else if (billingMode === 'Master Bill') {
                        if (reservationFinalRate !== '') {
                            const overrideVal = masterRoomOverrides[idx] !== undefined ? masterRoomOverrides[idx] : rPreDiscountBase;
                            rNet = overrideVal;
                            rDiscount = Math.max(0, rPreDiscountBase - rNet);
                            rTax = calculateRoomTaxFromOverride(activeTaxes, idx, rNet, dailyRatesByRoom, rExempt, roomsArr, isUae, hotel);
                        } else {
                            rNet = rPreDiscountBase;
                            rDiscount = 0;
                            rTax = calculateDynamicTax(activeTaxes, [idx], dailyRatesByRoom, rExempt, 'Daily', roomsArr, isUae, hotel);
                        }
                    }
                }

                let serviceCharge = 0;
                let municipalityFee = 0;
                let vat = 0;
                let tourismDirham = 0;
                let tourismDirhamRate = 15;
                let uaeBreakdown = null;

                if (isUae) {
                    const uae = calculateRoomUaeTaxBreakdown(rNet, diffDays, activeTaxes, hotel, rExempt);
                    rTax = uae.totalTax;
                    serviceCharge = uae.serviceCharge;
                    municipalityFee = uae.municipalityFee;
                    vat = uae.vat;
                    tourismDirham = uae.tourismDirham;
                    tourismDirhamRate = uae.tourismDirhamRate;
                    primaryTourismDirhamRate = tourismDirhamRate;
                    totalServiceCharge += serviceCharge;
                    totalMunicipalityFee += municipalityFee;
                    totalVat += vat;
                    totalTourismDirham += tourismDirham;
                    uaeBreakdown = {
                        serviceCharge,
                        municipalityFee,
                        vat,
                        tourismDirham,
                        totalTax: rTax,
                        serviceChargeRate: uae.serviceChargePct,
                        municipalityRate: uae.municipalityFeePct,
                        vatRate: uae.vatPct,
                        tourismDirhamRate: uae.tourismDirhamRate
                    };
                }

                const rRoomTotal = overrideMode === 'Daily' ? Math.max(rPreDiscountBase, rNet) : rPreDiscountBase;
                let rFinalTotal = rNet + rTax;
                let rFinal = rNet;

                let rTaxPreDiscount = calculateDynamicTax(activeTaxes, [idx], dailyRatesByRoom, rExempt, 'Daily', roomsArr, isUae, hotel);

                sumSubtotal += rRoomTotal;
                sumPreDiscountTax += rTaxPreDiscount;
                sumDiscount += rDiscount;
                sumTax += rTax;
                sumFinal += rFinalTotal;

                const isIndivFinal = overrideMode === 'Final' && billingMode === 'Individual Billing';

                return {
                    roomId: r.roomId,
                    roomTotal: rRoomTotal,
                    ...(rDiscount > 0 ? { discountAmount: Number(rDiscount.toFixed(2)) } : {}),
                    taxExempt: rExempt,
                    estimatedTax: Number(rTax.toFixed(2)),
                    finalTotal: Number(rFinalTotal.toFixed(2)),
                    ...(overrideMode === 'Final' && { finalRate: Number((isIndivFinal && finalRateByRoom[idx] ? Number(finalRateByRoom[idx]) : rFinal).toFixed(2)) }),
                    ...(isUae ? {
                        serviceCharge: Number(serviceCharge.toFixed(2)),
                        municipalityFee: Number(municipalityFee.toFixed(2)),
                        vat: Number(vat.toFixed(2)),
                        tourismDirham: Number(tourismDirham.toFixed(2)),
                        tourismDirhamRate: Number(tourismDirhamRate)
                    } : {})
                };
            });

            const finalSubtotal = Number(sumSubtotal.toFixed(2));
            const finalTax = Number(sumTax.toFixed(2));
            const finalDiscountAmount = Number(Math.max(0, sumDiscount).toFixed(2));
            const netTaxableAmount = Number(Math.max(0, finalSubtotal - finalDiscountAmount).toFixed(2));
            const finalTotalAmount = Number((finalSubtotal + finalTax).toFixed(2));
            const finalCalculatedAmount = Number((netTaxableAmount + finalTax).toFixed(2));
            const effectiveAdvance = billingMode === 'Master Bill' ? Number(advancePaid || 0) : 0;
            const finalBalanceDue = Number(Math.max(0, finalCalculatedAmount - effectiveAdvance).toFixed(2));

            // ----------------------------------

            const payload = {
                ...(bookingSource === 'Direct' && selectedManualSource && { bookingSourceId: selectedManualSource }),
                ...(bookingSource === 'Corporate' && linkExistingId && { bookingSourceId: linkExistingId }),
                ...(bookingSource === 'Corporate' && finalCorporateName && !linkExistingId && {
                    corporateName: finalCorporateName,
                    forceNewCorporateProfile: forceNewProfile
                }),
                isCorporate: bookingSource === 'Corporate',
                bookingType: "Group",
                directSource: bookingSource === 'Direct' ? 'Walk-in' : undefined,
                billingMode: billingMode === 'Master Bill' ? 'master' : 'individual',
                ...(isUae ? {
                    serviceCharge: Number(totalServiceCharge.toFixed(2)),
                    municipalityFee: Number(totalMunicipalityFee.toFixed(2)),
                    vat: Number(totalVat.toFixed(2)),
                    tourismDirham: Number(totalTourismDirham.toFixed(2)),
                    tourismDirhamRate: primaryTourismDirhamRate
                } : {}),
                groupName,
                organiser: {
                    title: undefined,
                    name: organiserName || undefined,
                    email: organiserEmail || undefined,
                    phone: organiserPhone || undefined,
                    phoneCode: organiserPhoneCode || undefined,
                    gender: undefined,
                    address: undefined,
                    zip: undefined,
                    city: undefined,
                    state: undefined,
                    country: undefined,
                    idProof: undefined,
                    idType: undefined,
                    idDocumentUrl: undefined,
                    issuingCountry: undefined,
                    issuingCity: undefined,
                    idExpiryDate: undefined,
                    birthDate: undefined,
                    birthCountry: undefined,
                    nationality: undefined
                },
                checkIn,
                checkOut,
                // rateOverride: null,
                rooms: processedRooms.map((r, idx) => {
                    const days = dailyRatesByRoom[idx] || [];

                    const mappedAdults = (r.processedGuests || []).filter(g => g.firstName || g.lastName || g.email || g.phone).map(g => ({
                        firstName: g.firstName || undefined,
                        lastName: g.lastName || undefined,
                        name: `${g.firstName || ''} ${g.lastName || ''}`.trim() || g.name || undefined,
                        email: g.email || undefined,
                        phoneCode: g.phoneCode || undefined,
                        phoneNumber: g.phone || undefined,
                        phone: g.phone || undefined,
                        guestType: "Adult",
                        nationality: g.nationality || undefined,
                        ...((g.address || g.city || g.state || g.country || g.zip) && {
                            address: {
                                addressLine1: g.address || undefined,
                                city: g.city || undefined,
                                state: g.state || undefined,
                                country: g.country || undefined,
                                zipCode: g.zip || undefined
                            }
                        }),
                        ...((g.idType || g.idNumber || g.uploadedIdUrl || g.idProofPreviewUrl) && {
                            identity: {
                                idType: g.idType || undefined,
                                idNumber: g.idNumber || undefined,
                                documentUrl: g.uploadedIdUrl || g.idProofPreviewUrl || undefined
                            }
                        })
                    }));

                    const mappedKids = (r.processedKids || []).slice(0, Number(r.kidsCount) || 0).filter(k => k.firstName || k.lastName).map(k => ({
                        firstName: k.firstName || undefined,
                        lastName: k.lastName || undefined,
                        name: `${k.firstName || ''} ${k.lastName || ''}`.trim() || undefined,
                        guestType: "Child",
                        childAge: k.age ? Number(k.age) : undefined,
                        birthDate: k.dob || undefined,
                        isPrimary: false,
                        ...((k.uploadedIdUrl || k.idProofPreviewUrl) && {
                            identity: {
                                documentUrl: k.uploadedIdUrl || k.idProofPreviewUrl || undefined
                            }
                        })
                    }));

                    const mappedGuests = [...mappedAdults, ...mappedKids];

                    return {
                        roomId: r.roomId,
                        taxExempt: taxExemptByRoom[idx] || taxExempt || false,
                        ratePlanId: r.ratePlanId || undefined,
                        adultsCount: Number(r.adultsCount),
                        kidsCount: Number(r.kidsCount),
                        extraBed: Boolean(r.extraBed),
                        ...(overrideReasons[idx] ? { reason: overrideReasons[idx] } : {}),
                        ...(overrideMode === 'Daily' && !appliedBulkByRoom[idx] ? {
                            dailyRates: (!days.some(d => d.overrideRate !== '')) ? [] : days.map(d => ({
                                date: d.date,
                                baseRate: Number(d.baseRate),
                                overrideRate: d.overrideRate !== '' ? Number(d.overrideRate) : undefined,
                                finalRate: d.overrideRate !== '' ? Number(d.overrideRate) : Number(d.baseRate)
                            }))
                        } : (overrideMode === 'Final' && billingMode === 'Master Bill') ? {
                            dailyRates: []
                        } : overrideMode !== 'Daily' ? {
                            
                        } : {}),
                        ...(mappedGuests.length > 0 && { guests: mappedGuests }),
                        ...(overrideMode === 'Final' && billingMode === 'Individual Billing' && finalRateByRoom[idx] !== undefined && finalRateByRoom[idx] !== '' ? {
                            rateOverride: {
                                type: 'FinalRateOverride',
                                value: Number(finalRateByRoom[idx])
                            }
                        } : (overrideMode === 'Daily' && appliedBulkByRoom[idx] ? {
                            rateOverride: {
                                type: appliedBulkByRoom[idx].type,
                                value: Number(appliedBulkByRoom[idx].discount)
                            }
                        } : (overrideMode === 'Daily' && days.some(d => d.overrideRate !== '') ? {
                            rateOverride: {
                                type: 'DailyRateOverride',
                                value: Number(days.reduce((s, d) => s + Number(d.overrideRate !== '' ? d.overrideRate : d.baseRate), 0).toFixed(2))
                            }
                        } : {})))
                    };
                }),
                ...(billingMode === 'Master Bill' && overrideMode === 'Final' && reservationFinalRate !== '' ? {
                    rateOverride: {
                        type: 'FinalRateOverride',
                        value: Number(reservationFinalRate),
                        reason: Object.values(overrideReasons).find(r => r && r.trim() !== '') || 'Master Group Final Override'
                    }
                } : {}),
                billing: {
                    subtotal: finalSubtotal,
                    taxExempted: finalGlobalExempt,
                    estimatedTax: finalTax,
                    totalAmount: finalTotalAmount,
                    discountAmount: finalDiscountAmount,
                    netTaxableAmount: netTaxableAmount,
                    finalAmount: finalCalculatedAmount,
                    advancePaid: effectiveAdvance,
                    balanceDue: finalBalanceDue,
                    ...(billingMode !== 'Master Bill' && { rooms: computedRoomsBilling }),
                    ...(isUae ? {
                        serviceCharge: Number(totalServiceCharge.toFixed(2)),
                        municipalityFee: Number(totalMunicipalityFee.toFixed(2)),
                        vat: Number(totalVat.toFixed(2)),
                        tourismDirham: Number(totalTourismDirham.toFixed(2)),
                        tourismDirhamRate: primaryTourismDirhamRate
                    } : {})
                },
                payment: {
                    billingMode: billingMode === 'Master Bill' ? 'master' : 'individual',
                    paymentMethod: paymentMethod || undefined,
                    advancePaid: effectiveAdvance,
                    balanceDue: finalBalanceDue
                }
            };

            if (editData) {
                const response = await ReservationService.update(editData.id, tenantId, payload);
                if (response?.pendingApproval || response?.data?.pendingApproval || response?.data?.data?.pendingApproval) {
                    onSuccess('Reservation updated. Your rate override has been submitted for Manager approval.');
                } else {
                    onSuccess('Group reservation updated successfully!');
                }
            } else {
                const response = await ReservationService.createGroup(payload, tenantId);
                if (response?.pendingApproval || response?.data?.pendingApproval || response?.data?.data?.pendingApproval) {
                    onSuccess('Rate override exceeds 40% and has been submitted for Manager approval.');
                } else {
                    onSuccess('Group reservation successfully created!');
                }
            }

            // Clean inputs
            resetForm();
        } catch (err) {
            console.error("Group Reservation creation failed:", err);

            // Check for 409 Conflict Disambiguation
            if (err.response?.status === 409 && err.response?.data?.data?.actionRequired === 'disambiguate') {
                const conflictData = err.response.data.data;
                setExistingCorporateMatch({ id: conflictData.existingId, name: finalCorporateName });
                setShowCorporateMatchModal(true);
                return;
            }

            const errMsg = err.response?.data?.message || err.message || "Failed to create group reservation";
            setError(errMsg);
            setToast({ open: true, message: errMsg, severity: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRoomChange = async (index, field, value) => {
        const newRooms = [...roomsArr];
        newRooms[index][field] = value;

        if (field === 'adultsCount') {
            const count = Number(value) || 1;
            if (newRooms[index].guests && newRooms[index].guests.length > count) {
                newRooms[index].guests = newRooms[index].guests.slice(0, count);
            }
        }
        if (field === 'kidsCount') {
            const count = Number(value) || 0;
            if (newRooms[index].kids && newRooms[index].kids.length > count) {
                newRooms[index].kids = newRooms[index].kids.slice(0, count);
            }
        }

        setReservationFinalRate('');

        if (field === 'roomId') {
            const selectedRoom = rooms.find(r => r.id === value);
            let fetchedPlans = null;

            if (selectedRoom) {
                const maxAdults = selectedRoom.maxAdults ?? selectedRoom.roomType?.maxAdults ?? 2;
                const maxKids = selectedRoom.maxKids ?? selectedRoom.roomType?.maxKids ?? 0;

                if (Number(newRooms[index].adultsCount) > maxAdults) {
                    newRooms[index].adultsCount = maxAdults;
                    if (newRooms[index].guests && newRooms[index].guests.length > maxAdults) {
                        newRooms[index].guests = newRooms[index].guests.slice(0, maxAdults);
                    }
                }
                if (Number(newRooms[index].kidsCount) > maxKids) {
                    newRooms[index].kidsCount = maxKids;
                    if (newRooms[index].kids && newRooms[index].kids.length > maxKids) {
                        newRooms[index].kids = newRooms[index].kids.slice(0, maxKids);
                    }
                }

                const roomTypeId = selectedRoom.roomTypeId || selectedRoom.roomType?.id;
                if (roomTypeId && !ratePlansByRoomType[roomTypeId]) {
                    setIsLoadingRates(true);
                    try {
                        const payload = {
                            roomTypeId,
                            startDate: checkIn,
                            endDate: checkOut
                        };
                        if (bookingSource) {
                            payload.Channel = bookingSource === 'Corporate' ? 'Corp' : 'Direct';
                        }
                        const ratesResult = await RateService.getRatePlans(tenantId, payload);
                        const ratesList = Array.isArray(ratesResult) ? ratesResult : (ratesResult.data || ratesResult.ratePlans || []);
                        const activeRatesList = ratesList.filter(plan => plan.status === 'Active');
                        fetchedPlans = activeRatesList;
                        setRatePlansByRoomType(prev => ({ ...prev, [roomTypeId]: activeRatesList }));
                    } catch (error) {
                        console.error("Failed to load rate plans for room type", error);
                    } finally {
                        setIsLoadingRates(false);
                    }
                }
            }

            const currentRoomTypeId = selectedRoom?.roomTypeId || selectedRoom?.roomType?.id;
            const plans = fetchedPlans || ratePlansByRoomType[currentRoomTypeId] || [];

            const defaultPlan = plans.find(p => p.type === 'Base') || plans[0];
            const currentPlanId = defaultPlan ? defaultPlan.id : '';
            newRooms[index].ratePlanId = currentPlanId;

            newRooms[index].rateAmount = calculateFinalRoomPrice({
                isBaseRate: defaultPlan?.type === 'Base',
                isDefault: defaultPlan ? defaultPlan.isDefault : false,
                roomRateAmount: selectedRoom?.baseRate,
                roomTypeAmount: selectedRoom?.roomType?.baseRate,
                customRatePlanAmount: defaultPlan?.rate || defaultPlan?.baseRate || 0
            });
            setReservationFinalRate('');
        }

        if (field === 'ratePlanId') {
            const selectedRoom = rooms.find(r => r.id === newRooms[index].roomId);
            const roomTypeId = selectedRoom?.roomTypeId || selectedRoom?.roomType?.id;
            const plans = ratePlansByRoomType[roomTypeId] || [];
            const plan = plans.find(p => p.id === value);

            newRooms[index].rateAmount = calculateFinalRoomPrice({
                isBaseRate: plan?.type === 'Base',
                isDefault: plan ? plan.isDefault : false,
                roomRateAmount: selectedRoom?.baseRate,
                roomTypeAmount: selectedRoom?.roomType?.baseRate,
                customRatePlanAmount: plan?.rate || plan?.baseRate || 0
            });
            setReservationFinalRate('');
        }

        setRoomsArr(newRooms);
    };

    const addRoom = () => {
        setRoomsArr([...roomsArr, {
            roomId: '', ratePlanId: '', rateAmount: '', adultsCount: 1, kidsCount: 0, extraBed: false,
            guests: [{ ...initialGuestState }], kids: []
        }]);
        setReservationFinalRate('');
    };

    const removeRoom = (index) => {
        const newRooms = [...roomsArr];
        newRooms.splice(index, 1);
        setRoomsArr(newRooms);
        setReservationFinalRate('');
    };

    const addGuest = (roomIndex) => {
        const newRooms = [...roomsArr];
        newRooms[roomIndex].guests.push({ ...initialGuestState });
        setRoomsArr(newRooms);
    };

    const removeGuest = (roomIndex, guestIndex) => {
        const newRooms = [...roomsArr];
        newRooms[roomIndex].guests.splice(guestIndex, 1);
        setRoomsArr(newRooms);
    };

    const handleGuestChange = (roomIndex, guestIndex, field, value) => {
        const newRooms = [...roomsArr];
        newRooms[roomIndex].guests[guestIndex][field] = value;
        setRoomsArr(newRooms);
    };

    return (
        <>
            <Modal open={open} onClose={(event, reason) => {
                if (reason === 'backdropClick') return;
                if (!isSubmitting && onClose) onClose();
            }}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', md: 1200, lg: 1300 }, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24,
                    maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column'
                }}>
                    <IconButton
                        sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        <CloseIcon />
                    </IconButton>

                    {isPrefilling ? (
                        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
                            <CircularProgress size={60} thickness={4} />
                            <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary', fontWeight: 'bold' }}>Loading Booking Details...</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            {/* Fixed Header Row */}
                            <Box sx={{ px: 4, pt: 3, pb: 2, borderBottom: '1px solid #e2e8f0', pr: 6, flexShrink: 0 }}>
                                <Typography variant="h6" fontWeight="bold" color="text.primary" mb={2}>
                                    Booking Information
                                </Typography>
                                <Stepper activeStep={activeStep} sx={{ '& .MuiStepLabel-label': { fontSize: '0.78rem' } }}>
                                    <Step><StepLabel>Booking Information</StepLabel></Step>
                                    <Step><StepLabel>Billing & Payment</StepLabel></Step>
                                </Stepper>
                            </Box>

                            {/* Scrollable Form Body */}
                            <Box sx={{ px: 4, py: 3, maxHeight: 'calc(90vh - 145px)', overflowY: 'auto', flexGrow: 1 }}>
                                <form onSubmit={(e) => e.preventDefault()}>
                            <Grid container spacing={3}>

                                {/* Group Meta Configuration */}
                                {activeStep === 0 && (
                                    <React.Fragment>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
                                                Group Meta Configuration
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={bookingSource === 'Direct' || bookingSource === 'Corporate' ? 3 : 6}>
                                            <FormControl fullWidth size="small" required disabled={isSubmitting || !!editData}>
                                                <InputLabel>Booking Channel</InputLabel>
                                                <Select
                                                    value={bookingSource} label="Booking Channel"
                                                    onChange={(e) => setBookingSource(e.target.value)}
                                                >
                                                    <MenuItem value="Direct">Direct</MenuItem>
                                                    <MenuItem value="Corporate">Corporate</MenuItem>
                                                    {bookingSource === 'OTA' && <MenuItem value="OTA">OTA (Imported)</MenuItem>}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {bookingSource === 'Direct' && (
                                            <Grid item xs={12} sm={3}>
                                                <FormControl fullWidth size="small" required disabled={isSubmitting}>
                                                    <InputLabel>Direct Source</InputLabel>
                                                    <Select
                                                        value={selectedManualSource} label="Direct Source"
                                                        onChange={(e) => setSelectedManualSource(e.target.value)}
                                                    >
                                                        {manualSources.map((source) => (
                                                            <MenuItem key={source.id} value={source.id}>{source.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        )}

                                        {bookingSource === 'Corporate' && (
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    fullWidth size="small" label="Corporate Name"
                                                    value={corporateName} onChange={(e) => setCorporateName(e.target.value)}
                                                    disabled={isSubmitting} required placeholder="e.g. Acme Corp"
                                                />
                                            </Grid>
                                        )}

                                        <Grid item xs={12} sm={bookingSource === 'Direct' || bookingSource === 'Corporate' ? 6 : 6}>
                                            <TextField
                                                fullWidth size="small" label="Group Name"
                                                value={groupName} onChange={(e) => setGroupName(e.target.value)}
                                                disabled={isSubmitting} required placeholder="e.g. Tech Solutions Workshop"
                                            />
                                        </Grid>

                                        <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                                        {/* Organiser Sub-Form */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
                                                Organiser Information
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={4}>
                                            <Autocomplete
                                                freeSolo
                                                filterOptions={(x) => x}
                                                options={guestOptions}
                                                getOptionLabel={(option) => {
                                                    if (typeof option === 'string') return option;
                                                    const contact = option.phoneNumber || option.phone || option.email || '';
                                                    return contact ? `${option.name || ''} (${contact})` : (option.name || '');
                                                }}
                                                inputValue={guestInputValue}
                                                onInputChange={(e, newInputValue, reason) => {
                                                    if (reason === 'input' || reason === 'clear') {
                                                        const cleanValue = newInputValue.replace(/[^a-zA-Z\s]/g, '');
                                                        setGuestInputValue(cleanValue);
                                                        setOrganiserName(cleanValue);
                                                    }
                                                }}
                                                onChange={(e, newValue) => {
                                                    if (typeof newValue === 'object' && newValue !== null) {
                                                        let cleanName = newValue.name || '';
                                                        cleanName = cleanName.replace(/\s*\(\d+\)/g, '').trim();

                                                        setOrganiserName(cleanName);
                                                        setOrganiserEmail(newValue.email || '');
                                                        const rawPhone = newValue.phone || newValue.phoneNumber || '';
                                                        const pCode = newValue.phoneCode || '';
                                                        if (pCode) {
                                                            setOrganiserPhoneCode(pCode);
                                                        }
                                                        const pMatch = rawPhone.match(/^(\+\d{1,4})\s*(.+)$/);
                                                        if (pMatch) {
                                                            if (!pCode) setOrganiserPhoneCode(pMatch[1]);
                                                            setOrganiserPhone(pMatch[2].replace(/\D/g, ''));
                                                        } else {
                                                            const digitsOnly = rawPhone.replace(/\D/g, '');
                                                            setOrganiserPhone(digitsOnly);
                                                            if (!pCode && isUae) {
                                                                setOrganiserPhoneCode('+971');
                                                            }
                                                        }
                                                        setGuestInputValue(cleanName);
                                                    } else if (typeof newValue === 'string') {
                                                        setOrganiserName(newValue);
                                                        setGuestInputValue(newValue);
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth size="small" label="Organiser Full Name"
                                                        disabled={isSubmitting} required
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            endAdornment: (
                                                                <React.Fragment>
                                                                    {guestSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                                    {params.InputProps.endAdornment}
                                                                </React.Fragment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                fullWidth size="small" label="Email Address" type="email"
                                                value={organiserEmail} onChange={(e) => setOrganiserEmail(e.target.value)}
                                                disabled={isSubmitting} required
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <FormControl size="small" sx={{ minWidth: 85 }} disabled={isSubmitting}>
                                                    <InputLabel>Code</InputLabel>
                                                    <Select value={organiserPhoneCode} label="Code" onChange={e => setOrganiserPhoneCode(e.target.value)}>
                                                        {SUPPORTED_PHONE_CODES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                                <TextField
                                                    fullWidth size="small" label="Phone Number"
                                                    value={organiserPhone}
                                                    onChange={(e) => setOrganiserPhone(e.target.value.replace(/\D/g, '').slice(0, 15))}
                                                    disabled={isSubmitting} required
                                                />
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                                        {/* Stay Horizon Pickers */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
                                                Group Stay Horizon
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth size="small" label="Check-in Date" type="date"
                                                value={checkIn} onChange={(e) => {
                                                    setCheckIn(e.target.value);
                                                    setReservationFinalRate('');
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                                disabled={isSubmitting || !!editData} required
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth size="small" label="Check-out Date" type="date"
                                                value={checkOut} onChange={(e) => {
                                                    setCheckOut(e.target.value);
                                                    setReservationFinalRate('');
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ min: checkIn || new Date().toISOString().split('T')[0] }}
                                                disabled={isSubmitting || !!editData} required
                                            />
                                        </Grid>

                                        <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                                        {/* Room Allocations & Guests */}
                                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                                                Dynamic Room Blocks Matrix
                                            </Typography>
                                            <Button size="small" startIcon={<AddIcon />} onClick={addRoom} disabled={isSubmitting}>
                                                Add Room
                                            </Button>
                                        </Grid>

                                        {roomsArr.map((r, roomIndex) => {
                                            const selectedRm = rooms.find(room => room.id === r.roomId);
                                            const maxAdults = selectedRm ? (selectedRm.maxAdults || selectedRm.roomType?.maxAdults) : null;
                                            const maxKids = selectedRm ? (selectedRm.maxKids !== undefined ? selectedRm.maxKids : selectedRm.roomType?.maxKids) : null;
                                            return (
                                                <Grid item xs={12} key={`room-${roomIndex}`}>
                                                <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider' }}>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary', fontWeight: 'bold' }}>
                                                            Room #{roomIndex + 1}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'center' }}>

                                                        <Autocomplete
                                                            size="small"
                                                            options={rooms.filter(room => !roomsArr.some((r2, idx2) => r2.roomId === (room.id || room._id) && idx2 !== roomIndex))}
                                                            getOptionLabel={(option) => `Room ${option.roomNumber} (${option.roomType?.name || option.type || 'Standard'})`}
                                                            value={rooms.find(room => (room.id || room._id) === r.roomId) || null}
                                                            isOptionEqualToValue={(option, value) => (option.id || option._id) === (value.id || value._id)}
                                                            onChange={(event, newValue) => {
                                                                handleRoomChange(roomIndex, 'roomId', newValue ? (newValue.id || newValue._id) : '');
                                                            }}
                                                            disabled={isSubmitting || isLoadingRooms || (!!editData && r.isExisting)}
                                                            noOptionsText={!checkIn || !checkOut ? 'Select dates first' : (isLoadingRooms ? 'Loading...' : 'No rooms available')}
                                                            renderInput={(params) => <TextField {...params} label="Room Assignment" required={!r.roomId} helperText={(!checkIn || !checkOut) ? 'Select check-in & check-out dates first' : ''} />}
                                                            sx={{ minWidth: 200, flexGrow: 1 }}
                                                        />

                                                        {r.roomId && checkIn && checkOut && (
                                                            <React.Fragment>
                                                                <FormControl size="small" sx={{ minWidth: 150 }} disabled={isSubmitting || isLoadingRates} required>
                                                                    <InputLabel>Rate Plan</InputLabel>
                                                                    <Select
                                                                        value={r.ratePlanId || ''} label="Rate Plan"
                                                                        onChange={(e) => handleRoomChange(roomIndex, 'ratePlanId', e.target.value)}
                                                                    >
                                                                        {isLoadingRates ? (
                                                                            <MenuItem disabled>Loading...</MenuItem>
                                                                        ) : (ratePlansByRoomType[rooms.find(room => room.id === r.roomId)?.roomTypeId || rooms.find(room => room.id === r.roomId)?.roomType?.id] || []).map((plan) => (
                                                                            <MenuItem key={plan.id} value={plan.id}>
                                                                                {plan.name || plan.ratePlanName}
                                                                            </MenuItem>
                                                                        ))}
                                                                        {(!isLoadingRates && (ratePlansByRoomType[rooms.find(room => room.id === r.roomId)?.roomTypeId || rooms.find(room => room.id === r.roomId)?.roomType?.id] || []).length === 0) && (
                                                                            <MenuItem disabled>No Rate Plans found</MenuItem>
                                                                        )}
                                                                    </Select>
                                                                </FormControl>

                                                                <TextField
                                                                    size="small" label={`Calc. Rate (${currencySymbol})`} type="number" onWheel={(e) => e.target.blur()}
                                                                    value={r.rateAmount} InputProps={{ readOnly: true }}
                                                                    disabled={isSubmitting} sx={{ minWidth: 110, flex: 1, bgcolor: 'rgba(0,0,0,0.03)' }}
                                                                />
                                                                <TextField
                                                                    size="small" label="Adults" type="number" value={r.adultsCount}
                                                                    onChange={(e) => { const val = e.target.value; if (val === '') { handleRoomChange(roomIndex, 'adultsCount', ''); return; } const num = Number(val); if (num < 1) { handleRoomChange(roomIndex, 'adultsCount', 1); return; } if (maxAdults && num > maxAdults) { setToast({ open: true, message: `Max allowed adults is ${maxAdults}.`, severity: 'warning' }); return; } handleRoomChange(roomIndex, 'adultsCount', num); }}
                                                                    onBlur={() => { if (!r.adultsCount || Number(r.adultsCount) < 1) handleRoomChange(roomIndex, 'adultsCount', 1); }}
                                                                    onWheel={(e) => e.target.blur()} disabled={isSubmitting} sx={{ minWidth: 90, flex: 1 }}
                                                                />

                                                                <TextField
                                                                    size="small" label="Kids" type="number" value={r.kidsCount}
                                                                    onChange={(e) => { const val = e.target.value; if (val === '') { handleRoomChange(roomIndex, 'kidsCount', ''); return; } const num = Number(val); if (num < 0) { handleRoomChange(roomIndex, 'kidsCount', 0); return; } if (maxKids !== null && maxKids !== undefined && num > maxKids) { setToast({ open: true, message: `Max allowed kids is ${maxKids}.`, severity: 'warning' }); return; } handleRoomChange(roomIndex, 'kidsCount', num); }}
                                                                    onBlur={() => { if (r.kidsCount === '' || Number(r.kidsCount) < 0) handleRoomChange(roomIndex, 'kidsCount', 0); }}
                                                                    onWheel={(e) => e.target.blur()} disabled={isSubmitting} sx={{ minWidth: 90, flex: 1 }}
                                                                />

                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={Boolean(r.extraBed)}
                                                                            onChange={(e) => handleRoomChange(roomIndex, 'extraBed', e.target.checked)}
                                                                            disabled={isSubmitting}
                                                                            size="small"
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label={<Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Extra Bed</Typography>}
                                                                    sx={{ ml: 0.5, mr: 0.5 }}
                                                                />

                                                            </React.Fragment>
                                                        )}
                                                        {roomsArr.length > 1 && (
                                                            <IconButton color="error" onClick={() => removeRoom(roomIndex)} disabled={isSubmitting} sx={{ ml: 'auto' }}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        )}
                                                        </Box>
                                                    </Box>

                                                    {/* Guests Array Repeater */}
                                                    {r.roomId && (
                                                        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Box>
                                                                <Typography variant="subtitle2" fontWeight="bold">Guest Information</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {(r.guests ? r.guests.filter(g => g.firstName?.trim() || g.lastName?.trim()).length : 0) + (r.kids ? r.kids.filter(k => k.firstName?.trim() || k.lastName?.trim() || k.age).length : 0)} of {Number(r.adultsCount || 1) + Number(r.kidsCount || 0)} guests detailed
                                                                </Typography>
                                                            </Box>
                                                            <Button
                                                                variant="outlined" size="small" startIcon={<PersonAddIcon />}
                                                                onClick={() => {
                                                                    setActiveRoomIndexForGuestModal(roomIndex);
                                                                    setGuestModalError(null);
                                                                    setShowGuestModal(true);
                                                                    setActiveGuestTab(0);
                                                                }}
                                                                disabled={isSubmitting}
                                                            >
                                                                Add / Edit Guests
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Paper>
                                            </Grid>
                                        );
                                        })}
                                    </React.Fragment>
                                )}

                                {/* Group Rate Management & Billing Summary */}
                                {activeStep === 1 && checkIn && checkOut && roomsArr.some(r => r.roomId) && (() => {
                                    const today = new Date().setHours(0, 0, 0, 0);
                                    const isPastDate = (dateString) => new Date(dateString).setHours(0, 0, 0, 0) < today;
                                    const isRateLocked = bookingSource === 'OTA' || editData?.status === 'Checked-out';

                                    const activeRates = dailyRatesByRoom[selectedRateRoomIndex] || [];

                                    const targetRooms = roomsArr.map((r, i) => r.roomId ? i : -1).filter(i => i !== -1);
                                    const totalPreBaseAll = targetRooms.reduce((sum, i) => sum + (dailyRatesByRoom[i] || []).reduce((s, d) => s + Number(d.baseRate || 0), 0), 0);

                                    const masterRoomOverridesUI = {};
                                    if (overrideMode === 'Final' && billingMode === 'Master Bill' && reservationFinalRate !== '') {
                                        const totalOverrideVal = Number(reservationFinalRate);
                                        let allocatedSoFar = 0;
                                        targetRooms.forEach((i, tIdx) => {
                                            const roomBase = (dailyRatesByRoom[i] || []).reduce((s, d) => s + Number(d.baseRate || 0), 0);
                                            if (tIdx === targetRooms.length - 1) {
                                                masterRoomOverridesUI[i] = Math.max(0, Math.round((totalOverrideVal - allocatedSoFar) * 100) / 100);
                                            } else {
                                                const prop = totalPreBaseAll > 0 ? (roomBase / totalPreBaseAll) : (1 / (targetRooms.length || 1));
                                                const val = Math.round(totalOverrideVal * prop * 100) / 100;
                                                masterRoomOverridesUI[i] = val;
                                                allocatedSoFar += val;
                                            }
                                        });
                                    }

                                    // Pre-calculate per-room billing and discount details
                                    const roomBillingList = roomsArr.map((r, idx) => {
                                        if (!r.roomId) return null;
                                        const days = dailyRatesByRoom[idx] || [];
                                        const nights = days.length || 1;
                                        const rPreDiscountBase = days.reduce((s, d) => s + Number(d.baseRate || 0), 0);
                                        const rExempt = billingMode === 'Individual Billing' ? (taxExemptByRoom[idx] || false) : taxExempt;
                                        const rDailyNet = days.reduce((s, d) => s + Number(overrideMode === 'Daily' && d.overrideRate !== '' ? d.overrideRate : d.baseRate), 0);
                                        const rDailyTax = calculateDynamicTax(activeTaxes, [idx], dailyRatesByRoom, rExempt, 'Daily', roomsArr, isUae, hotel);
                                        let rPreTax = rDailyTax;

                                        let rDiscount = 0;
                                        let rTax = 0;
                                        let rNet = rPreDiscountBase;
                                        let rFinalTotal = 0;

                                        if (overrideMode === 'Daily') {
                                            rNet = rDailyNet;
                                            rDiscount = Math.max(0, rPreDiscountBase - rDailyNet);
                                            rTax = calculateDynamicTax(activeTaxes, [idx], dailyRatesByRoom, rExempt, 'Daily', roomsArr, isUae, hotel);
                                            rFinalTotal = rNet + rTax;
                                        } else if (overrideMode === 'Final') {
                                            if (billingMode === 'Individual Billing') {
                                                const rFinalRate = finalRateByRoom[idx] !== undefined && finalRateByRoom[idx] !== '' ? finalRateByRoom[idx] : '';
                                                if (rFinalRate !== '') {
                                                    const overrideVal = Math.min(rPreDiscountBase, Number(rFinalRate));
                                                    rNet = overrideVal;
                                                    rDiscount = Math.max(0, rPreDiscountBase - rNet);
                                                    rTax = calculateRoomTaxFromOverride(activeTaxes, idx, rNet, dailyRatesByRoom, rExempt, roomsArr, isUae, hotel);
                                                } else {
                                                    rNet = rPreDiscountBase;
                                                    rDiscount = 0;
                                                    rTax = rPreTax;
                                                }
                                                rFinalTotal = rNet + rTax;
                                            } else {
                                                if (reservationFinalRate !== '') {
                                                    const overrideVal = masterRoomOverridesUI[idx] !== undefined ? masterRoomOverridesUI[idx] : rPreDiscountBase;
                                                    rNet = overrideVal;
                                                    rDiscount = Math.max(0, rPreDiscountBase - rNet);
                                                    rTax = calculateRoomTaxFromOverride(activeTaxes, idx, rNet, dailyRatesByRoom, rExempt, roomsArr, isUae, hotel);
                                                } else {
                                                    rNet = rPreDiscountBase;
                                                    rDiscount = 0;
                                                    rTax = rPreTax;
                                                }
                                                rFinalTotal = rNet + rTax;
                                            }
                                        }

                                        let rUae = null;
                                        if (isUae) {
                                            const uae = calculateRoomUaeTaxBreakdown(rNet, nights, activeTaxes, hotel, rExempt);
                                            rTax = uae.totalTax;
                                            rUae = {
                                                serviceCharge: uae.serviceCharge,
                                                municipalityFee: uae.municipalityFee,
                                                vat: uae.vat,
                                                tourismDirham: uae.tourismDirham,
                                                totalTax: uae.totalTax,
                                                serviceChargeRate: uae.serviceChargePct,
                                                municipalityRate: uae.municipalityFeePct,
                                                vatRate: uae.vatPct,
                                                tourismDirhamRate: uae.tourismDirhamRate
                                            };
                                        }

                                        rNet = Math.round(rNet * 100) / 100;
                                        rDiscount = Math.round(rDiscount * 100) / 100;
                                        rTax = Math.round(rTax * 100) / 100;
                                        rPreTax = Math.round(rPreTax * 100) / 100;
                                        rFinalTotal = Math.round((rNet + rTax) * 100) / 100;

                                        return {
                                            idx,
                                            rPreDiscountBase,
                                            rDailyNet,
                                            rNet,
                                            rTax,
                                            rPreTax,
                                            rDiscount,
                                            rFinalTotal,
                                            uaeBreakdown: rUae
                                        };
                                    });

                                    const uaeSummaryBreakdown = isUae ? (() => {
                                        let sc = 0, mun = 0, vat = 0, td = 0, tdRate = 15, scPct = 10, munPct = 7, vatPct = 5;
                                        roomBillingList.forEach(r => {
                                            if (r?.uaeBreakdown) {
                                                sc += r.uaeBreakdown.serviceCharge;
                                                mun += r.uaeBreakdown.municipalityFee;
                                                vat += r.uaeBreakdown.vat;
                                                td += r.uaeBreakdown.tourismDirham;
                                                scPct = r.uaeBreakdown.serviceChargeRate || r.uaeBreakdown.serviceChargePct || 10;
                                                munPct = r.uaeBreakdown.municipalityRate || r.uaeBreakdown.municipalityFeePct || 7;
                                                vatPct = r.uaeBreakdown.vatRate || r.uaeBreakdown.vatPct || 5;
                                                tdRate = r.uaeBreakdown.tourismDirhamRate || 15;
                                            }
                                        });
                                        return {
                                            serviceCharge: Number(sc.toFixed(2)),
                                            serviceChargePct: scPct,
                                            serviceChargeRate: scPct,
                                            municipalityFee: Number(mun.toFixed(2)),
                                            municipalityFeePct: munPct,
                                            municipalityRate: munPct,
                                            vat: Number(vat.toFixed(2)),
                                            vatPct: vatPct,
                                            vatRate: vatPct,
                                            tourismDirham: Number(td.toFixed(2)),
                                            tourismDirhamRate: tdRate,
                                            totalTax: Number((sc + mun + vat + td).toFixed(2))
                                        };
                                    })() : null;

                                    const totalBaseSubtotal = Math.round(roomBillingList.reduce((sum, r) => sum + (r ? (overrideMode === 'Daily' ? Math.max(r.rPreDiscountBase, r.rDailyNet) : r.rPreDiscountBase) : 0), 0) * 100) / 100;
                                    const totalDiscount = Math.round(roomBillingList.reduce((sum, r) => sum + (r ? r.rDiscount : 0), 0) * 100) / 100;
                                    const totalNetTaxable = Math.round(roomBillingList.reduce((sum, r) => sum + (r ? r.rNet : 0), 0) * 100) / 100;
                                    const taxAmt = Math.round(roomBillingList.reduce((sum, r) => sum + (r ? r.rTax : 0), 0) * 100) / 100;
                                    const displayFinalGroupTotal = (Math.round(roomBillingList.reduce((sum, r) => sum + (r ? r.rFinalTotal : 0), 0) * 100) / 100).toFixed(2);
                                    const totalReservationFinalAmount = displayFinalGroupTotal;
                                    const maxGroupTotal = totalBaseSubtotal;

                                    const handleRevertRate = () => {
                                        setDailyRatesByRoom(prev => {
                                            const next = { ...prev };
                                            if (next[selectedRateRoomIndex]) {
                                                next[selectedRateRoomIndex] = next[selectedRateRoomIndex].map(d => {
                                                    if (isPastDate(d.date)) return d;
                                                    return { ...d, overrideRate: '' };
                                                });
                                            }
                                            return next;
                                        });
                                        setAppliedBulkByRoom(prev => {
                                            const next = { ...prev };
                                            delete next[selectedRateRoomIndex];
                                            return next;
                                        });
                                        setBulkDiscount('');
                                        setReservationFinalRate('');
                                    };

                                    const applyBulk = () => {
                                        if (!bulkDiscount) return;
                                        setDailyRatesByRoom(prev => {
                                            const next = { ...prev };
                                            const applyToIdxs = bulkApplyScope === 'Entire Group' ? Object.keys(next) : [selectedRateRoomIndex];

                                            applyToIdxs.forEach(idx => {
                                                next[idx] = next[idx].map(d => {
                                                    if (isPastDate(d.date)) return d;
                                                    const base = d.baseRate;
                                                    const override = bulkDiscountType === 'Percentage'
                                                        ? Math.max(0, base - (base * Number(bulkDiscount) / 100)).toFixed(2)
                                                        : Math.max(0, base - Number(bulkDiscount)).toFixed(2);
                                                    return { ...d, overrideRate: override };
                                                });
                                            });
                                            return next;
                                        });
                                        setAppliedBulkByRoom(prev => {
                                            const next = { ...prev };
                                            const applyToIdxs = bulkApplyScope === 'Entire Group' ? Object.keys(dailyRatesByRoom) : [selectedRateRoomIndex];
                                            applyToIdxs.forEach(idx => {
                                                next[idx] = { discount: bulkDiscount, type: bulkDiscountType };
                                            });
                                            return next;
                                        });
                                        setReservationFinalRate('');
                                    };

                                    const isCurrentBulkApplied = appliedBulkByRoom[selectedRateRoomIndex]?.discount === bulkDiscount && appliedBulkByRoom[selectedRateRoomIndex]?.type === bulkDiscountType;

                                    return (
                                        <React.Fragment>
                                            {/* Left Column: Rate Management */}
                                            <Grid item xs={12} md={8}>
                                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                            <Typography variant="subtitle1" fontWeight="bold">Rate Management</Typography>
                                                            <ToggleButtonGroup
                                                                color="primary"
                                                                value={overrideMode}
                                                                exclusive
                                                                onChange={(e, newMode) => {
                                                                    if (newMode !== null) {
                                                                        setOverrideMode(newMode);
                                                                        if (!editData) {
                                                                            if (newMode === 'Final') {
                                                                                const clearedDailyRates = {};
                                                                                Object.keys(dailyRatesByRoom).forEach(idx => {
                                                                                    clearedDailyRates[idx] = dailyRatesByRoom[idx].map(d => ({ ...d, overrideRate: '' }));
                                                                                });
                                                                                setDailyRatesByRoom(clearedDailyRates);
                                                                            } else {
                                                                                setFinalRateByRoom({});
                                                                                setReservationFinalRate('');
                                                                            }
                                                                            setAppliedBulkByRoom({});
                                                                            setOverrideReasons({});
                                                                            setBulkDiscount('');
                                                                        } else {
                                                                            if (newMode === 'Daily') {
                                                                                setDailyRatesByRoom(originalDailyRatesByRoom);
                                                                            }
                                                                        }
                                                                    }
                                                                }}
                                                                size="small"
                                                            >
                                                                <ToggleButton value="Daily" sx={{ textTransform: 'none', py: 0.5 }}>Daily Override</ToggleButton>
                                                                <ToggleButton value="Final" sx={{ textTransform: 'none', py: 0.5 }}>Final Override</ToggleButton>
                                                            </ToggleButtonGroup>
                                                        </Box>

                                                        {isRateLocked && (
                                                            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff3cd', color: '#856404', borderRadius: 1, border: '1px solid #ffeeba', fontSize: '0.85rem' }}>
                                                                {editData?.status === 'Checked-out'
                                                                    ? <span><strong>Folio Closed:</strong> Rates are locked because this reservation has been checked out.</span>
                                                                    : <span><strong>OTA Lock:</strong> Rates imported from an OTA cannot be manually overridden.</span>
                                                                }
                                                            </Box>
                                                        )}

                                                        {/* Room Chips */}
                                                        {roomsArr.filter(r => r.roomId).length > 0 && (
                                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                                                                {roomsArr.map((r, i) => {
                                                                    if (!r.roomId) return null;
                                                                    const rm = rooms.find(room => room.id === r.roomId);
                                                                    const roomName = rm ? `Room ${rm.roomNumber} (${rm.roomType?.name || 'Standard'})` : `Room ${i + 1}`;
                                                                    const rB = roomBillingList[i];
                                                                    return (
                                                                        <Box key={i} onClick={() => setSelectedRateRoomIndex(i)}
                                                                            sx={{
                                                                                px: 2, py: 0.75, borderRadius: 5, border: '1px solid #cbd5e1', cursor: 'pointer', fontSize: '0.85rem',
                                                                                bgcolor: selectedRateRoomIndex === i ? '#e2e8f0' : '#fff',
                                                                                fontWeight: selectedRateRoomIndex === i ? 600 : 400,
                                                                                display: 'flex', alignItems: 'center', gap: 1
                                                                            }}>
                                                                            <span>{roomName}</span>
                                                                            {overrideMode === 'Daily' && rB && rB.rDiscount > 0 && (
                                                                                <Box component="span" sx={{ fontSize: '0.72rem', bgcolor: '#dcfce7', color: '#166534', px: 0.8, py: 0.2, borderRadius: 1, fontWeight: 700 }}>
                                                                                    {formatCurrency(rB.rDiscount, currency)}
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        )}

                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                            <Typography variant="subtitle2" fontWeight="bold">Daily Rate Breakdown</Typography>
                                                            {overrideMode === 'Daily' && (() => {
                                                                const rB = roomBillingList[selectedRateRoomIndex];
                                                                if (rB && rB.rDiscount > 0) {
                                                                    const pct = rB.rPreDiscountBase > 0 ? ((rB.rDiscount / rB.rPreDiscountBase) * 100).toFixed(0) : 0;
                                                                    return (
                                                                        <Typography variant="caption" sx={{ bgcolor: '#dcfce7', color: '#166534', px: 1, py: 0.25, borderRadius: 1, fontWeight: 700 }}>
                                                                            Override Active: {formatCurrency(rB.rDiscount, currency)} ({pct}% Off)
                                                                        </Typography>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </Box>
                                                        {activeRates.length === 0 ? (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Select dates and rooms in Step 1 to see daily rates.</Typography>
                                                        ) : (
                                                            <Box sx={{ width: '100%', overflowX: 'auto', mb: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                                                                    <Box component="thead">
                                                                        <Box component="tr" sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                                                                            {['Day', 'Base Rate', overrideMode === 'Daily' ? `Override Rate (${currencySymbol})` : null, overrideMode === 'Daily' ? 'Discount' : null, isUae ? 'Tax' : 'Tax Slab', isUae ? null : 'GST', isUae ? 'Tax Amount' : 'CGST', isUae ? null : 'SGST'].filter(Boolean).map(h => (
                                                                                <Box component="th" key={h} sx={{ p: 1.5, textAlign: 'left', fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</Box>
                                                                            ))}
                                                                        </Box>
                                                                    </Box>
                                                                    <Box component="tbody">
                                                                        {activeRates.map((d, i) => {
                                                                            const roomObj = roomsArr[selectedRateRoomIndex];
                                                                            const extraBedCharge = Number(roomObj?.extraBedCharge || (roomObj?.extraBed ? (roomObj?.extraBedPrice || roomObj?.extraBedRate || 0) : 0));
                                                                            const baseRate = Number(d.baseRate || 0);
                                                                            const extraCharge = d.extraBedCharge !== undefined ? Number(d.extraBedCharge) : extraBedCharge;
                                                                            const grv = baseRate + extraCharge;
                                                                            const finalRate = overrideMode === 'Daily' && d.overrideRate !== '' ? Number(d.overrideRate) : grv;
                                                                            
                                                                            const currentRoomExempt = billingMode === 'Individual Billing' ? (taxExemptByRoom[selectedRateRoomIndex] || false) : taxExempt;
                                                                            let taxName = '—', slabStr = '', gst = 0, cgstAmt = 0, sgstAmt = 0, rowTaxAmt = 0;
                                                                            
                                                                            if (!currentRoomExempt) {
                                                                                if (isUae) {
                                                                                    const uaeRates = extractUaeCustomRates(activeTaxes);
                                                                                    const scPct = uaeRates.serviceChargePct !== undefined ? uaeRates.serviceChargePct : DUBAI_DEFAULT_RATES.serviceChargePct;
                                                                                    const munPct = uaeRates.municipalityFeePct !== undefined ? uaeRates.municipalityFeePct : DUBAI_DEFAULT_RATES.municipalityFeePct;
                                                                                    const vatPct = uaeRates.vatPct !== undefined ? uaeRates.vatPct : DUBAI_DEFAULT_RATES.vatPct;
                                                                                    const tdRate = uaeRates.tourismDirhamRate !== undefined ? uaeRates.tourismDirhamRate : (hotel.rating >= 5 ? 20 : 15);
                                                                                    
                                                                                    const daySC = Number(((finalRate * scPct) / 100).toFixed(2));
                                                                                    const dayMun = Number(((finalRate * munPct) / 100).toFixed(2));
                                                                                    const dayVat = Number((((finalRate + daySC + dayMun) * vatPct) / 100).toFixed(2));
                                                                                    const dayTD = (i < 30) ? tdRate : 0;
                                                                                    rowTaxAmt = Number((daySC + dayMun + dayVat + dayTD).toFixed(2));
                                                                                    taxName = 'Dubai Levies & Fees';
                                                                                    gst = Number((((scPct + munPct + ((100 + scPct + munPct) * vatPct / 100)) * 10) / 10).toFixed(1));
                                                                                    slabStr = `SC ${scPct}% + Mun ${munPct}% + VAT ${vatPct}% + TD ${currencySymbol}${dayTD}`;
                                                                                } else {
                                                                                    let matchingTax = null;
                                                                                    for (const t of activeTaxes) {
                                                                                        if (t.serviceType === 'room' || t.serviceType === 'All' || t.serviceType === 'accommodation' || t.applicableTo === 'Room' || t.applicableTo === 'All') {
                                                                                            const min = t.minThreshold ? Number(t.minThreshold) : 0;
                                                                                            const max = t.maxThreshold ? Number(t.maxThreshold) : Infinity;
                                                                                            if (grv >= min && grv <= max) {
                                                                                                matchingTax = t;
                                                                                                break;
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                    if (matchingTax) {
                                                                                        gst = Number(matchingTax.gstPct || matchingTax.rate || 0);
                                                                                        cgstAmt = (finalRate * Number(matchingTax.cgstPct || matchingTax.cgst || (gst / 2))) / 100;
                                                                                        sgstAmt = (finalRate * Number(matchingTax.sgstPct || matchingTax.sgst || (gst / 2))) / 100;
                                                                                        rowTaxAmt = (finalRate * gst) / 100;
                                                                                        const min = matchingTax.minThreshold ? Number(matchingTax.minThreshold) : 0;
                                                                                        const max = matchingTax.maxThreshold ? Number(matchingTax.maxThreshold) : Infinity;
                                                                                        slabStr = max === Infinity ? `> ${formatCurrency(min, currency)}` : `${formatCurrency(min, currency)} - ${formatCurrency(max, currency)}`;
                                                                                        taxName = `GST ${gst}%`;
                                                                                    }
                                                                                }
                                                                            }

                                                                            return (
                                                                                <Box component="tr" key={d.date} sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                                    <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>Day {i + 1}</Box>
                                                                                    <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{formatCurrency(d.baseRate, currency)}</Box>
                                                                                    {overrideMode === 'Daily' && (
                                                                                        <Box component="td" sx={{ p: 1 }}>
                                                                                            <TextField size="small" type="number" onWheel={(e) => e.target.blur()} value={d.overrideRate}
                                                                                                onChange={e => {
                                                                                                    if (Number(e.target.value) < 0) return;
                                                                                                    setReservationFinalRate('');
                                                                                                    setDailyRatesByRoom(prev => {
                                                                                                        const next = { ...prev };
                                                                                                        next[selectedRateRoomIndex] = [...next[selectedRateRoomIndex]];
                                                                                                        next[selectedRateRoomIndex][i] = { ...next[selectedRateRoomIndex][i], overrideRate: e.target.value };
                                                                                                        return next;
                                                                                                    });
                                                                                                    setAppliedBulkByRoom(prev => {
                                                                                                        const next = { ...prev };
                                                                                                        delete next[selectedRateRoomIndex];
                                                                                                        return next;
                                                                                                    });
                                                                                                }}
                                                                                                placeholder={currencySymbol} disabled={isSubmitting || isRateLocked || isPastDate(d.date) || enableBulkRateOverride}
                                                                                                InputProps={{ inputProps: { min: 0, step: '0.01' } }}
                                                                                                sx={{ width: 90 }}
                                                                                            />
                                                                                        </Box>
                                                                                    )}
                                                                                    {overrideMode === 'Daily' && (
                                                                                        <Box component="td" sx={{ p: 1.5, whiteSpace: 'nowrap' }}>
                                                                                            {d.overrideRate !== '' ? (
                                                                                                (() => {
                                                                                                    const diff = grv - finalRate;
                                                                                                    if (diff > 0) {
                                                                                                        const pct = grv > 0 ? ((diff / grv) * 100).toFixed(0) : 0;
                                                                                                        return (
                                                                                                            <Box>
                                                                                                                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 700 }}>
                                                                                                                    {formatCurrency(diff, currency)}
                                                                                                                </Typography>
                                                                                                                <Typography variant="caption" sx={{ color: 'success.dark', display: 'block', fontSize: '0.72rem', fontWeight: 600 }}>
                                                                                                                    {pct}% off
                                                                                                                </Typography>
                                                                                                            </Box>
                                                                                                        );
                                                                                                    } else {
                                                                                                        return <Typography variant="body2" color="text.secondary">—</Typography>;
                                                                                                    }
                                                                                                })()
                                                                                            ) : (
                                                                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                                                                            )}
                                                                                        </Box>
                                                                                    )}
                                                                                    <Box component="td" sx={{ p: 1.5 }}>
                                                                                        {taxName !== '—' ? (
                                                                                            <>
                                                                                                <Typography variant="body2" fontWeight={600} fontSize="0.8rem">{taxName}</Typography>
                                                                                                {slabStr && <Typography variant="caption" color="text.secondary" display="block">({slabStr})</Typography>}
                                                                                            </>
                                                                                        ) : (
                                                                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                                                                        )}
                                                                                    </Box>
                                                                                    {!isUae && (
                                                                                        <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{gst > 0 ? `${gst}%` : '—'}</Box>
                                                                                    )}
                                                                                    {isUae ? (
                                                                                        <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{formatCurrency(rowTaxAmt, currency)}</Box>
                                                                                    ) : (
                                                                                        <>
                                                                                            <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{formatCurrency(cgstAmt, currency)}</Box>
                                                                                            <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{formatCurrency(sgstAmt, currency)}</Box>
                                                                                        </>
                                                                                    )}
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                        {(() => {
                                                                            let totalBase = 0, totalOverride = 0, totalCgst = 0, totalSgst = 0, totalTaxAmt = 0;
                                                                            const currentRoomExempt = billingMode === 'Individual Billing' ? (taxExemptByRoom[selectedRateRoomIndex] || false) : taxExempt;
                                                                            activeRates.forEach((row, i) => {
                                                                                const roomObj = roomsArr[selectedRateRoomIndex];
                                                                                const extraBedCharge = Number(roomObj?.extraBedCharge || (roomObj?.extraBed ? (roomObj?.extraBedPrice || roomObj?.extraBedRate || 0) : 0));
                                                                                const baseRate = Number(row.baseRate || 0);
                                                                                const extraCharge = row.extraBedCharge !== undefined ? Number(row.extraBedCharge) : extraBedCharge;
                                                                                const grv = baseRate + extraCharge;
                                                                                const finalRate = row.overrideRate !== '' ? Number(row.overrideRate) : grv;
                                                                                totalBase += baseRate;
                                                                                totalOverride += finalRate;
                                                                                
                                                                                let cgstAmt = 0, sgstAmt = 0, rowTax = 0;
                                                                                if (!currentRoomExempt) {
                                                                                    if (isUae) {
                                                                                        const uaeRates = extractUaeCustomRates(activeTaxes);
                                                                                        const scPct = uaeRates.serviceChargePct !== undefined ? uaeRates.serviceChargePct : DUBAI_DEFAULT_RATES.serviceChargePct;
                                                                                        const munPct = uaeRates.municipalityFeePct !== undefined ? uaeRates.municipalityFeePct : DUBAI_DEFAULT_RATES.municipalityFeePct;
                                                                                        const vatPct = uaeRates.vatPct !== undefined ? uaeRates.vatPct : DUBAI_DEFAULT_RATES.vatPct;
                                                                                        const tdRate = uaeRates.tourismDirhamRate !== undefined ? uaeRates.tourismDirhamRate : (hotel.rating >= 5 ? 20 : 15);
                                                                                        
                                                                                        const daySC = Number(((finalRate * scPct) / 100).toFixed(2));
                                                                                        const dayMun = Number(((finalRate * munPct) / 100).toFixed(2));
                                                                                        const dayVat = Number((((finalRate + daySC + dayMun) * vatPct) / 100).toFixed(2));
                                                                                        const dayTD = (i < 30) ? tdRate : 0;
                                                                                        rowTax = Number((daySC + dayMun + dayVat + dayTD).toFixed(2));
                                                                                    } else {
                                                                                        let matchingTax = null;
                                                                                        for (const t of activeTaxes) {
                                                                                            if (t.serviceType === 'room' || t.serviceType === 'All' || t.serviceType === 'accommodation' || t.applicableTo === 'Room' || t.applicableTo === 'All') {
                                                                                                const min = t.minThreshold ? Number(t.minThreshold) : 0;
                                                                                                const max = t.maxThreshold ? Number(t.maxThreshold) : Infinity;
                                                                                                if (grv >= min && grv <= max) {
                                                                                                    matchingTax = t; break;
                                                                                                }
                                                                                            }
                                                                                        }
                                                                                        if (matchingTax) {
                                                                                            const gst = Number(matchingTax.gstPct || matchingTax.rate || 0);
                                                                                            cgstAmt = (finalRate * Number(matchingTax.cgstPct || matchingTax.cgst || (gst / 2))) / 100;
                                                                                            sgstAmt = (finalRate * Number(matchingTax.sgstPct || matchingTax.sgst || (gst / 2))) / 100;
                                                                                            rowTax = (finalRate * gst) / 100;
                                                                                        }
                                                                                    }
                                                                                }
                                                                                totalCgst += cgstAmt;
                                                                                totalSgst += sgstAmt;
                                                                                totalTaxAmt += rowTax;
                                                                            });
                                                                            return (
                                                                                <Box component="tr" sx={{ bgcolor: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                                                                                    <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>TOTAL</Box>
                                                                                    <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{formatCurrency(totalBase, currency)}</Box>
                                                                                    {overrideMode === 'Daily' && (
                                                                                        <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 'bold', color: '#2563eb', whiteSpace: 'nowrap' }}>{formatCurrency(totalOverride, currency)}</Box>
                                                                                    )}
                                                                                    {overrideMode === 'Daily' && (
                                                                                        <Box component="td" sx={{ p: 1.5, whiteSpace: 'nowrap' }}>
                                                                                            {(() => {
                                                                                                const totalDisc = Math.max(0, totalBase - totalOverride);
                                                                                                if (totalDisc > 0) {
                                                                                                    const pct = totalBase > 0 ? ((totalDisc / totalBase) * 100).toFixed(0) : 0;
                                                                                                    return (
                                                                                                        <Box>
                                                                                                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                                                                                                {formatCurrency(totalDisc, currency)}
                                                                                                            </Typography>
                                                                                                            <Typography variant="caption" sx={{ color: 'success.dark', display: 'block', fontSize: '0.72rem', fontWeight: 700 }}>
                                                                                                                {pct}% off
                                                                                                            </Typography>
                                                                                                        </Box>
                                                                                                    );
                                                                                                }
                                                                                                return <Typography variant="body2" color="text.secondary">—</Typography>;
                                                                                            })()}
                                                                                        </Box>
                                                                                    )}
                                                                                    <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>—</Box>
                                                                                    {!isUae && <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>—</Box>}
                                                                                    {isUae ? (
                                                                                        <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{formatCurrency(totalTaxAmt, currency)}</Box>
                                                                                    ) : (
                                                                                        <>
                                                                                            <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{formatCurrency(totalCgst, currency)}</Box>
                                                                                            <Box component="td" sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{formatCurrency(totalSgst, currency)}</Box>
                                                                                        </>
                                                                                    )}
                                                                                </Box>
                                                                            );
                                                                        })()}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        {overrideMode === 'Daily' && (
                                                            <React.Fragment>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: enableBulkRateOverride ? 2 : 1, mt: 2, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                                                                    <Typography variant="subtitle2" fontWeight="bold">Bulk Rate Override</Typography>
                                                                    <Switch
                                                                        checked={enableBulkRateOverride}
                                                                        onChange={(e) => {
                                                                            setEnableBulkRateOverride(e.target.checked);
                                                                            if (!e.target.checked) {
                                                                                setBulkDiscount('');
                                                                                setAppliedBulkByRoom(prev => {
                                                                                    const next = { ...prev };
                                                                                    delete next[selectedRateRoomIndex];
                                                                                    return next;
                                                                                });
                                                                            }
                                                                        }}
                                                                        size="small"
                                                                        color="primary"
                                                                        sx={{ mr: -1 }}
                                                                    />
                                                                </Box>
                                                                {enableBulkRateOverride && (
                                                                    <>
                                                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5 }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                <Box component="input" type="radio" name="bulkType" checked={bulkDiscountType === 'Percentage'} onChange={() => setBulkDiscountType('Percentage')} sx={{ m: 0 }} />
                                                                                <Typography variant="body2">Percentage (%)</Typography>
                                                                            </Box>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                <Box component="input" type="radio" name="bulkType" checked={bulkDiscountType === 'Fixed'} onChange={() => setBulkDiscountType('Fixed')} sx={{ m: 0 }} />
                                                                                <Typography variant="body2">Fixed</Typography>
                                                                            </Box>
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                                                            <TextField size="small" fullWidth type="number" onWheel={(e) => e.target.blur()} value={bulkDiscount} onChange={e => { if (Number(e.target.value) < 0) return; setBulkDiscount(e.target.value) }} placeholder={bulkDiscountType === 'Percentage' ? "Percentage override" : "Fixed amount override"} disabled={isSubmitting || isRateLocked} />
                                                                            <Button variant="contained" onClick={applyBulk} disabled={isRateLocked || !bulkDiscount || isSubmitting || isCurrentBulkApplied} sx={{ whiteSpace: 'nowrap', boxShadow: 'none', px: 3 }}>Apply</Button>
                                                                            {(appliedBulkByRoom[selectedRateRoomIndex] || (activeRates && activeRates.some(d => d.overrideRate !== ''))) && (
                                                                                <Button variant="outlined" color="error" onClick={handleRevertRate} disabled={isSubmitting || isRateLocked} sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}>Revert</Button>
                                                                            )}
                                                                        </Box>
                                                                    </>
                                                                )}
                                                                
                                                                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                                                                    <Typography variant="subtitle2" fontWeight="bold" mb={1.5}>Override Reason</Typography>
                                                                    <TextField fullWidth size="small" label="Reason" multiline rows={2} value={overrideReasons[selectedRateRoomIndex] || ''} onChange={e => setOverrideReasons(prev => ({ ...prev, [selectedRateRoomIndex]: e.target.value }))} disabled={isSubmitting || isRateLocked} />
                                                                </Box>
                                                            </React.Fragment>
                                                        )}
                                                    </Paper>
                                                </Grid>

                                                <Grid item xs={12} md={4}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                    <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                                                        <Box sx={{ bgcolor: '#f8fafc', p: 2, borderBottom: '1px solid #e2e8f0' }}>
                                                            <Typography variant="subtitle1" fontWeight="bold">Group Stay Details</Typography>
                                                        </Box>
                                                        <Box sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">Check-in</Typography>
                                                                <Typography variant="body2" fontWeight="bold">{new Date(checkIn + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">Check-out</Typography>
                                                                <Typography variant="body2" fontWeight="bold">{new Date(checkOut + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
                                                            </Box>
                                                            <Typography variant="body2" color="text.secondary" mt={2}>Total Rooms: <Box component="span" fontWeight="bold" color="text.primary">{roomsArr.filter(r => r.roomId).length}</Box></Typography>
                                                        </Box>
                                                    </Paper>

                                                {/* BILLING MODE */}
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1 }}>
                                                        BILLING MODE
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                                        {/* Master Folio Button */}
                                                        <Box 
                                                            onClick={() => {
                                                                if (billingMode !== 'Master Bill') {
                                                                    const anyExempt = Object.values(taxExemptByRoom).some(v => v);
                                                                    setTaxExempt(anyExempt);
                                                                    setTaxExemptByRoom({});
                                                                    setBillingMode('Master Bill');
                                                                }
                                                            }}
                                                            sx={{ 
                                                                flex: 1, 
                                                                py: 1.2, 
                                                                px: 1.5, 
                                                                borderRadius: 2.5, 
                                                                border: billingMode === 'Master Bill' ? '1.5px solid #16a34a' : '1.5px solid #e2e8f0', 
                                                                bgcolor: billingMode === 'Master Bill' ? '#ecfdf5' : '#ffffff', 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: 1, 
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s ease',
                                                                '&:hover': { bgcolor: billingMode === 'Master Bill' ? '#ecfdf5' : '#f8fafc' }
                                                            }}
                                                        >
                                                            {billingMode === 'Master Bill' ? (
                                                                <RadioButtonCheckedIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                                                            ) : (
                                                                <RadioButtonUncheckedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                                            )}
                                                            <Typography sx={{ fontWeight: billingMode === 'Master Bill' ? 700 : 500, color: billingMode === 'Master Bill' ? '#15803d' : '#475569', fontSize: '0.85rem' }}>
                                                                Master Folio
                                                            </Typography>
                                                        </Box>

                                                        {/* Individual Folios Button */}
                                                        <Box 
                                                            onClick={() => {
                                                                if (roomsArr.length > 1 && billingMode !== 'Individual Billing') {
                                                                    const frObj = {};
                                                                    const teObj = {};
                                                                    const len = roomsArr.length || 1;
                                                                    for (let i = 0; i < len; i++) {
                                                                        frObj[i] = '';
                                                                        teObj[i] = taxExempt;
                                                                    }
                                                                    setFinalRateByRoom(frObj);
                                                                    setTaxExemptByRoom(teObj);
                                                                    setTaxExempt(false);
                                                                    setAdvancePaid('');
                                                                    setBillingMode('Individual Billing');
                                                                }
                                                            }}
                                                            sx={{ 
                                                                flex: 1, 
                                                                py: 1.2, 
                                                                px: 1.5, 
                                                                borderRadius: 2.5, 
                                                                border: billingMode === 'Individual Billing' ? '1.5px solid #16a34a' : '1.5px solid #e2e8f0', 
                                                                bgcolor: billingMode === 'Individual Billing' ? '#ecfdf5' : '#ffffff', 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: 1, 
                                                                cursor: roomsArr.length > 1 ? 'pointer' : 'not-allowed',
                                                                opacity: roomsArr.length > 1 ? 1 : 0.6,
                                                                transition: 'all 0.15s ease',
                                                                '&:hover': { bgcolor: billingMode === 'Individual Billing' ? '#ecfdf5' : '#f8fafc' }
                                                            }}
                                                        >
                                                            {billingMode === 'Individual Billing' ? (
                                                                <RadioButtonCheckedIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                                                            ) : (
                                                                <RadioButtonUncheckedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                                            )}
                                                            <Typography sx={{ fontWeight: billingMode === 'Individual Billing' ? 700 : 500, color: billingMode === 'Individual Billing' ? '#15803d' : '#475569', fontSize: '0.85rem' }}>
                                                                Individual Folios
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                {/* Section Title: Billing Summary */}
                                                <Box sx={{ mb: 1.5 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                                        Billing Summary
                                                    </Typography>
                                                </Box>

                                                {/* Billing Summary Cards */}
                                                {billingMode === 'Individual Billing' ? (
                                                    <Box>
                                                        {roomsArr.map((r, i) => {
                                                            if (!r.roomId) return null;
                                                            const rB = roomBillingList[i];
                                                            if (!rB) return null;
                                                            const rFinalRate = finalRateByRoom[i] !== undefined ? finalRateByRoom[i] : '';
                                                            const rExempt = taxExemptByRoom[i] || false;
                                                            const rWarning = finalRateWarningByRoom[i] || false;
                                                            const rm = rooms.find(room => room.id === r.roomId);
                                                            const roomTypeName = rm?.roomType?.name || rm?.type || (r.roomTypeId ? (ratePlansByRoomType[r.roomTypeId]?.[0]?.roomType?.name) : null) || 'Room';
                                                            const roomNumber = rm?.roomNumber || (rm?.roomCode ? rm.roomCode : null);

                                                            const days = dailyRatesByRoom[i] || [];
                                                            const extraBedCharge = Number(r?.extraBedCharge || (r?.extraBed ? (r?.extraBedPrice || r?.extraBedRate || 0) : 0));
                                                            let firstMatchingTax = null;
                                                            for (const d of days) {
                                                                const baseRate = Number(d.baseRate || 0);
                                                                const extraCharge = d.extraBedCharge !== undefined ? Number(d.extraBedCharge) : extraBedCharge;
                                                                const grv = baseRate + extraCharge;
                                                                for (const t of activeTaxes) {
                                                                    if (t.serviceType === 'room' || t.serviceType === 'All') {
                                                                        const min = t.minThreshold ? Number(t.minThreshold) : 0;
                                                                        const max = t.maxThreshold ? Number(t.maxThreshold) : Infinity;
                                                                        if (grv >= min && grv <= max) {
                                                                            firstMatchingTax = t;
                                                                            break;
                                                                        }
                                                                    }
                                                                }
                                                                if (firstMatchingTax) break;
                                                            }
                                                            const gstPct = (taxExempt || taxExemptByRoom[i]) ? 0 : (firstMatchingTax?.gstPct !== undefined ? firstMatchingTax.gstPct : (rB.rNet > 0 && rB.rTax > 0 ? Math.round((rB.rTax / rB.rNet) * 100) : 0));

                                                            const handleTeChange = (val) => {
                                                                setTaxExemptByRoom(prev => ({ ...prev, [i]: val }));
                                                            };

                                                            const handleFrChange = (val) => {
                                                                if (val !== '' && Number(val) < 0) return;
                                                                let parsedVal = val;
                                                                if (val !== '' && Number(val) > rB.rPreDiscountBase) {
                                                                    parsedVal = rB.rPreDiscountBase.toString();
                                                                    setFinalRateWarningByRoom(prev => ({ ...prev, [i]: true }));
                                                                    setTimeout(() => setFinalRateWarningByRoom(prev => ({ ...prev, [i]: false })), 3000);
                                                                } else {
                                                                    setFinalRateWarningByRoom(prev => ({ ...prev, [i]: false }));
                                                                }
                                                                setFinalRateByRoom(prev => ({ ...prev, [i]: parsedVal }));
                                                            };

                                                            const roomRateDisplay = overrideMode === 'Daily' ? Math.max(rB.rPreDiscountBase, rB.rDailyNet) : rB.rPreDiscountBase;

                                                            return (
                                                                <Paper key={i} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, overflow: 'hidden', mb: 2, bgcolor: '#ffffff' }}>
                                                                    <Box sx={{ bgcolor: '#ecfdf5', px: 2, py: 1.25, borderBottom: '1px solid #d1fae5', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <HotelIcon sx={{ color: '#16a34a', fontSize: 18 }} />
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#15803d', fontSize: '0.875rem' }}>
                                                                            {roomNumber ? `Room ${roomNumber} · ${roomTypeName}` : `Room ${i + 1} · ${roomTypeName}`}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ p: 2 }}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.85rem' }}>Room Rate</Typography>
                                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>{formatCurrency(roomRateDisplay, currency)}</Typography>
                                                                        </Box>

                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.85rem' }}>Discount Applied</Typography>
                                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: rB.rDiscount > 0 ? "#16a34a" : "#64748b", fontSize: '0.85rem' }}>
                                                                                {rB.rDiscount > 0 ? `-${formatCurrency(rB.rDiscount, currency)}` : formatCurrency(0, currency)}
                                                                            </Typography>
                                                                        </Box>

                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.85rem' }}>Net Taxable Value</Typography>
                                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>{formatCurrency(rB.rNet, currency)}</Typography>
                                                                        </Box>

                                                                        {isUae && rB.uaeBreakdown ? (
                                                                            <Box sx={{ mb: 1.5, p: 1, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem' }}>Service Charge ({rB.uaeBreakdown.serviceChargeRate}%)</Typography>
                                                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.78rem' }}>{formatCurrency(rB.uaeBreakdown.serviceCharge, currency)}</Typography>
                                                                                </Box>
                                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem' }}>Municipality Fee ({rB.uaeBreakdown.municipalityRate}%)</Typography>
                                                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.78rem' }}>{formatCurrency(rB.uaeBreakdown.municipalityFee, currency)}</Typography>
                                                                                </Box>
                                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem' }}>UAE VAT ({rB.uaeBreakdown.vatRate}%)</Typography>
                                                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.78rem' }}>{formatCurrency(rB.uaeBreakdown.vat, currency)}</Typography>
                                                                                </Box>
                                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem' }}>Tourism Dirham Fee</Typography>
                                                                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.78rem' }}>{formatCurrency(rB.uaeBreakdown.tourismDirham, currency)}</Typography>
                                                                                </Box>
                                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px dashed #cbd5e1' }}>
                                                                                    <Typography variant="caption" sx={{ color: '#1e293b', fontWeight: 700, fontSize: '0.8rem' }}>Total Dubai Levies</Typography>
                                                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#2563eb', fontSize: '0.8rem' }}>{formatCurrency(rB.rTax, currency)}</Typography>
                                                                                </Box>
                                                                            </Box>
                                                                        ) : (
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                                                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.85rem' }}>Room Tax Amount</Typography>
                                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#2563eb', fontSize: '0.85rem' }}>
                                                                                    {formatCurrency(rB.rTax, currency)} {rExempt ? '(Exempt)' : (isUae ? '(Taxes & VAT)' : (gstPct > 0 ? `(${gstPct}% GST)` : ''))}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}

                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                                            <Box component="input" type="checkbox" checked={rExempt} onChange={e => handleTeChange(e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#16a34a' }} />
                                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>Tax Exemption</Typography>
                                                                        </Box>

                                                                        <Divider sx={{ my: 1.5 }} />

                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '0.9rem' }}>Room Total</Typography>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#16a34a', fontSize: '1.05rem' }}>{formatCurrency(rB.rFinalTotal, currency)}</Typography>
                                                                        </Box>

                                                                        {overrideMode === 'Final' && (
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 1.5, borderTop: '1px dashed #e2e8f0' }}>
                                                                                <Box>
                                                                                    <Typography variant="body2" color="text.secondary">Final Rate Override</Typography>
                                                                                    {rWarning && (
                                                                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, fontWeight: 500, display: 'block' }}>Exceeds base rate</Typography>
                                                                                    )}
                                                                                    {rFinalRate !== '' && Number(rFinalRate) < rB.rPreDiscountBase && authData.role === 'Receptionist' && (((rB.rPreDiscountBase - Number(rFinalRate)) / rB.rPreDiscountBase) * 100 > 40) && (
                                                                                        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, fontWeight: 500, display: 'block' }}>Discount exceeds 40%. Manager approval required.</Typography>
                                                                                    )}
                                                                                </Box>
                                                                                <TextField size="small" type="number" onWheel={(e) => e.target.blur()} value={rFinalRate}
                                                                                    onChange={e => handleFrChange(e.target.value)} disabled={isSubmitting || isRateLocked} placeholder="Manual"
                                                                                    InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>, inputProps: { min: 0, max: rB.rPreDiscountBase, step: '0.01' } }} sx={{ width: 120 }} />
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                </Paper>
                                                            );
                                                        })}

                                                        {/* Grand Total Bottom Card */}
                                                        <Paper elevation={0} sx={{ border: '1.5px solid #bbf7d0', borderRadius: 2.5, p: 2, mb: 2, bgcolor: '#ecfdf5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1rem' }}>Grand Total</Typography>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#16a34a', fontSize: '1.2rem' }}>
                                                                {formatCurrency(Number(displayFinalGroupTotal), currency)}
                                                            </Typography>
                                                        </Paper>
                                                    </Box>
                                                ) : (
                                                    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, mb: 2, bgcolor: '#ffffff' }}>
                                                        <Box sx={{ p: 2 }}>
                                                            {roomsArr.length > 0 && (
                                                                <Box sx={{ mb: 2 }}>
                                                                    <Box 
                                                                        onClick={() => setIsRoomBreakdownOpen(prev => !prev)}
                                                                        sx={{ 
                                                                            display: 'flex', 
                                                                            alignItems: 'center', 
                                                                            gap: 0.5, 
                                                                            cursor: 'pointer', 
                                                                            color: '#16a34a', 
                                                                            fontWeight: 700, 
                                                                            fontSize: '0.85rem',
                                                                            mb: 1.5,
                                                                            userSelect: 'none',
                                                                            '&:hover': { opacity: 0.85 }
                                                                        }}
                                                                    >
                                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a' }}>
                                                                            Room Breakdown
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a', fontSize: '0.75rem' }}>
                                                                            {isRoomBreakdownOpen ? '▲' : '▼'}
                                                                        </Typography>
                                                                    </Box>

                                                                    <Collapse in={isRoomBreakdownOpen}>
                                                                        {roomsArr.map((r, i) => {
                                                                            if (!r.roomId) return null;
                                                                            const rB = roomBillingList[i];
                                                                            if (!rB) return null;

                                                                            const rm = rooms.find(room => room.id === r.roomId);
                                                                            const roomTypeName = rm?.roomType?.name || rm?.type || (r.roomTypeId ? (ratePlansByRoomType[r.roomTypeId]?.[0]?.roomType?.name) : null) || 'Room';
                                                                            const days = dailyRatesByRoom[i] || [];
                                                                            const nights = days.length || 1;
                                                                            const roomLabel = `Room ${i + 1} (${roomTypeName}) x ${nights}N`;

                                                                            const roomRate = overrideMode === 'Daily' ? Math.max(rB.rPreDiscountBase, rB.rDailyNet) : rB.rPreDiscountBase;
                                                                            const roomDiscount = rB.rDiscount;
                                                                            const roomNet = rB.rNet;
                                                                            const roomTax = rB.rTax;
                                                                            const roomFinalTotal = rB.rFinalTotal;

                                                                            // Calculate GST percentage for this room
                                                                            const extraBedCharge = Number(r?.extraBedCharge || (r?.extraBed ? (r?.extraBedPrice || r?.extraBedRate || 0) : 0));
                                                                            let firstMatchingTax = null;
                                                                            for (const d of days) {
                                                                                const baseRate = Number(d.baseRate || 0);
                                                                                const extraCharge = d.extraBedCharge !== undefined ? Number(d.extraBedCharge) : extraBedCharge;
                                                                                const grv = baseRate + extraCharge;
                                                                                for (const t of activeTaxes) {
                                                                                    if (t.serviceType === 'room' || t.serviceType === 'All') {
                                                                                        const min = t.minThreshold ? Number(t.minThreshold) : 0;
                                                                                        const max = t.maxThreshold ? Number(t.maxThreshold) : Infinity;
                                                                                        if (grv >= min && grv <= max) {
                                                                                            firstMatchingTax = t;
                                                                                            break;
                                                                                        }
                                                                                    }
                                                                                }
                                                                                if (firstMatchingTax) break;
                                                                            }
                                                                            const rExempt = rB.taxExempt !== undefined ? rB.taxExempt : Boolean(taxExempt || taxExemptByRoom[i]);
                                                                            const gstPct = rExempt ? 0 : (firstMatchingTax?.gstPct !== undefined ? firstMatchingTax.gstPct : (roomNet > 0 && roomTax > 0 ? Math.round((roomTax / roomNet) * 100) : 0));

                                                                            return (
                                                                                <Box key={i} sx={{ p: 1.5, mb: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                                                                    <Typography variant="body2" fontWeight={700} color="#1e293b" sx={{ mb: 1 }}>
                                                                                        {roomLabel}
                                                                                    </Typography>

                                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                        <Typography variant="caption" color="text.secondary">Room Rate</Typography>
                                                                                        <Typography variant="caption" fontWeight={600} color="text.primary">{formatCurrency(roomRate, currency)}</Typography>
                                                                                    </Box>

                                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                        <Typography variant="caption" color="text.secondary">Discount Applied</Typography>
                                                                                        <Typography variant="caption" fontWeight={600} color={roomDiscount > 0 ? "success.main" : "text.secondary"}>
                                                                                            {roomDiscount > 0 ? `-${formatCurrency(roomDiscount, currency)}` : formatCurrency(0, currency)}
                                                                                        </Typography>
                                                                                    </Box>

                                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                        <Typography variant="caption" color="text.secondary">Net Taxable Value</Typography>
                                                                                        <Typography variant="caption" fontWeight={700} color="text.primary">{formatCurrency(roomNet, currency)}</Typography>
                                                                                    </Box>

                                                                                    {isUae && rB.uaeBreakdown ? (
                                                                                        <Box sx={{ mb: 0.75, p: 1, bgcolor: '#f1f5f9', borderRadius: 1 }}>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Service Charge ({rB.uaeBreakdown.serviceChargeRate}%)</Typography>
                                                                                                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>{formatCurrency(rB.uaeBreakdown.serviceCharge, currency)}</Typography>
                                                                                            </Box>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Municipality Fee ({rB.uaeBreakdown.municipalityRate}%)</Typography>
                                                                                            <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>{formatCurrency(rB.uaeBreakdown.municipalityFee, currency)}</Typography>
                                                                                            </Box>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>UAE VAT ({rB.uaeBreakdown.vatRate}%)</Typography>
                                                                                                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>{formatCurrency(rB.uaeBreakdown.vat, currency)}</Typography>
                                                                                            </Box>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Tourism Dirham</Typography>
                                                                                                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>{formatCurrency(rB.uaeBreakdown.tourismDirham, currency)}</Typography>
                                                                                            </Box>
                                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px dashed #cbd5e1' }}>
                                                                                                <Typography variant="caption" fontWeight={700} color="#1e293b" sx={{ fontSize: '0.75rem' }}>Total Dubai Levies</Typography>
                                                                                                <Typography variant="caption" fontWeight={700} color="#2563eb" sx={{ fontSize: '0.75rem' }}>{formatCurrency(roomTax, currency)}</Typography>
                                                                                            </Box>
                                                                                        </Box>
                                                                                    ) : (
                                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                                                                            <Typography variant="caption" color="text.secondary">Room Tax Amount</Typography>
                                                                                            <Typography variant="caption" fontWeight={600} color="#2563eb">
                                                                                                {formatCurrency(roomTax, currency)} {rExempt ? '(Exempt)' : (isUae ? '(Taxes & VAT)' : (gstPct > 0 ? `(${gstPct}% GST)` : ''))}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    )}

                                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.75, borderTop: '1px dashed #e2e8f0' }}>
                                                                                        <Typography variant="caption" fontWeight={700} color="#1e293b">Room Total</Typography>
                                                                                        <Typography variant="caption" fontWeight={800} color="#16a34a">{formatCurrency(roomFinalTotal, currency)}</Typography>
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </Collapse>
                                                                    <Divider sx={{ my: 1.5 }} />
                                                                </Box>
                                                            )}

                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">Room Subtotal</Typography>
                                                                <Typography variant="body2" fontWeight={700}>{formatCurrency(totalBaseSubtotal, currency)}</Typography>
                                                            </Box>

                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                                                <Typography variant="body2" color="text.secondary">Less : Override / Discount</Typography>
                                                                <Typography variant="body2" color={totalDiscount > 0 ? "success.main" : "text.secondary"} fontWeight={700}>
                                                                    {totalDiscount > 0 ? `-${formatCurrency(totalDiscount, currency)}` : formatCurrency(0, currency)}
                                                                </Typography>
                                                            </Box>

                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">Net Taxable Value</Typography>
                                                                <Typography variant="body2" fontWeight={700}>{formatCurrency(totalNetTaxable, currency)}</Typography>
                                                            </Box>

                                                            {isUae && uaeSummaryBreakdown ? (
                                                                <Box sx={{ mb: 1.5, p: 1.25, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                        <Typography variant="caption" color="text.secondary">Service Charge ({uaeSummaryBreakdown.serviceChargeRate}%)</Typography>
                                                                        <Typography variant="caption" fontWeight={600}>{formatCurrency(uaeSummaryBreakdown.serviceCharge, currency)}</Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                        <Typography variant="caption" color="text.secondary">Municipality Fee ({uaeSummaryBreakdown.municipalityRate}%)</Typography>
                                                                        <Typography variant="caption" fontWeight={600}>{formatCurrency(uaeSummaryBreakdown.municipalityFee, currency)}</Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                        <Typography variant="caption" color="text.secondary">UAE VAT ({uaeSummaryBreakdown.vatRate}%)</Typography>
                                                                        <Typography variant="caption" fontWeight={600}>{formatCurrency(uaeSummaryBreakdown.vat, currency)}</Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                        <Typography variant="caption" color="text.secondary">Tourism Dirham Fee</Typography>
                                                                        <Typography variant="caption" fontWeight={600}>{formatCurrency(uaeSummaryBreakdown.tourismDirham, currency)}</Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px dashed #cbd5e1' }}>
                                                                        <Typography variant="body2" fontWeight={700} color="text.secondary">Estimated Taxes & Levies</Typography>
                                                                        <Typography variant="body2" fontWeight={700} color="#2563eb">{formatCurrency(taxAmt, currency)}</Typography>
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                    <Typography variant="body2" color="text.secondary">Estimated Tax</Typography>
                                                                    <Typography variant="body2" fontWeight={700} color="#2563eb">{formatCurrency(taxAmt, currency)}</Typography>
                                                                </Box>
                                                            )}

                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1.5, p: 1, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Box component="input" type="checkbox" checked={taxExempt} onChange={e => { setTaxExempt(e.target.checked); }} style={{ width: 18, height: 18, cursor: 'pointer' }} />
                                                                    <Typography variant="body2" fontWeight={600} color="text.primary">Tax Exemption</Typography>
                                                                </Box>
                                                            </Box>

                                                            <Divider sx={{ my: 1.5 }} />

                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">Final Reservation Total</Typography>
                                                                <Typography variant="subtitle2" fontWeight={800} color="#0f172a" fontSize="1rem">
                                                                    {formatCurrency(Number(displayFinalGroupTotal), currency)}
                                                                </Typography>
                                                            </Box>

                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                                                                <Typography variant="subtitle2" fontWeight={800} color="#0f172a">Balance Due</Typography>
                                                                <Typography variant="subtitle2" fontWeight={800} color="#16a34a" fontSize="1.1rem">
                                                                    {formatCurrency(Math.max(0, Number(displayFinalGroupTotal) - Number(advancePaid || 0)), currency)}
                                                                </Typography>
                                                            </Box>

                                                            {overrideMode === 'Final' && (
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: '1px dashed #e2e8f0' }}>
                                                                    <Box>
                                                                        <Typography variant="body2" color="text.secondary">Final Rate Override</Typography>
                                                                        {finalRateWarning && (
                                                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, fontWeight: 500, display: 'block' }}>Exceeds base rate</Typography>
                                                                        )}
                                                                        {reservationFinalRate !== '' && Number(reservationFinalRate) < totalBaseSubtotal && authData.role === 'Receptionist' && (((totalBaseSubtotal - Number(reservationFinalRate)) / totalBaseSubtotal) * 100 > 40) && (
                                                                            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, fontWeight: 500, display: 'block' }}>Discount exceeds 40%. Manager approval required.</Typography>
                                                                        )}
                                                                    </Box>
                                                                    <TextField size="small" type="number" onWheel={(e) => e.target.blur()} value={reservationFinalRate}
                                                                        onChange={e => {
                                                                            let val = e.target.value;
                                                                            if (val !== '' && Number(val) < 0) return;
                                                                            if (val !== '' && Number(val) > maxGroupTotal) {
                                                                                val = maxGroupTotal.toString();
                                                                                setFinalRateWarning(true);
                                                                                setTimeout(() => setFinalRateWarning(false), 3000);
                                                                            } else {
                                                                                setFinalRateWarning(false);
                                                                            }
                                                                            setReservationFinalRate(val);
                                                                        }} disabled={isSubmitting || isRateLocked} placeholder="Manual"
                                                                        InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>, inputProps: { min: 0, max: maxGroupTotal, step: '0.01' } }} sx={{ width: 120 }} />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Paper>
                                                )}

                                                <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                                                    <Box sx={{ bgcolor: '#f8fafc', p: 2, borderBottom: '1px solid #e2e8f0' }}>
                                                        <Typography variant="subtitle1" fontWeight="bold">Payment Details</Typography>
                                                    </Box>
                                                    <Box sx={{ p: 2 }}>
                                                        <FormControl fullWidth size="small" disabled={isSubmitting || isLoadingPaymentMethods} sx={{ mb: billingMode === 'Master Bill' ? 2 : 0 }}>
                                                            <InputLabel>Payment Method</InputLabel>
                                                            <Select 
                                                                value={paymentMethod} 
                                                                label="Payment Method" 
                                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                                                disabled={isSubmitting || isLoadingPaymentMethods}
                                                            >
                                                                {isLoadingPaymentMethods ? (
                                                                    <MenuItem disabled value="">
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <CircularProgress size={16} />
                                                                            <Typography variant="body2">Loading payment methods...</Typography>
                                                                        </Box>
                                                                    </MenuItem>
                                                                ) : paymentMethods.length > 0 ? (
                                                                    paymentMethods.map((method, idx) => {
                                                                        const val = typeof method === 'string' ? method : (method.name || method.code || method.id);
                                                                        const label = typeof method === 'string' ? method : (method.label || method.name || method.code || val);
                                                                        return (
                                                                            <MenuItem key={method.id || method._id || val || idx} value={val}>
                                                                                {label}
                                                                            </MenuItem>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <MenuItem disabled value="">No payment methods available</MenuItem>
                                                                )}
                                                                {paymentMethod && !paymentMethods.some(m => (typeof m === 'string' ? m : (m.name || m.code || m.id)) === paymentMethod) && (
                                                                    <MenuItem key={paymentMethod} value={paymentMethod}>
                                                                        {paymentMethod}
                                                                    </MenuItem>
                                                                )}
                                                            </Select>
                                                        </FormControl>
                                                        {billingMode === 'Master Bill' && (
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                type="number"
                                                                label="Advance Paid"
                                                                placeholder="0.00"
                                                                value={advancePaid}
                                                                onChange={e => {
                                                                    if (Boolean(editData)) return;
                                                                    const val = e.target.value;
                                                                    if (val !== '' && Number(val) < 0) return;
                                                                    setAdvancePaid(val);
                                                                }}
                                                                disabled={isSubmitting}
                                                                InputProps={{
                                                                    readOnly: Boolean(editData),
                                                                    startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                                                                    inputProps: { min: 0, step: '0.01' }
                                                                }}
                                                                sx={{ ...(Boolean(editData) && { bgcolor: 'rgba(0,0,0,0.04)' }) }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Paper>
                                                </Box>
                                            </Grid>
                                        </React.Fragment>
                                    );
                                })()}
                            </Grid>

                            {error && (
                                <Box sx={{ mt: 2 }}>
                                    <Alert severity="error">{error}</Alert>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                                {activeStep === 0 ? (
                                    <React.Fragment>
                                        <Button
                                            fullWidth variant="outlined"
                                            type="button"
                                            onClick={onClose} disabled={isSubmitting}
                                            sx={{ borderRadius: 8, py: 1.25, fontWeight: 700, textTransform: 'none', fontSize: '1rem', color: 'text.secondary', borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            fullWidth variant="contained"
                                            type="button"
                                            onClick={() => {
                                                const errorMsg = validateForm();
                                                if (errorMsg) {
                                                    setError(errorMsg);
                                                    setToast({ open: true, message: errorMsg, severity: 'warning' });
                                                } else {
                                                    setError(null);
                                                    setActiveStep(1);
                                                }
                                            }}
                                            sx={{ borderRadius: 8, py: 1.25, fontWeight: 700, textTransform: 'none', fontSize: '1rem', boxShadow: '0 4px 12px rgba(59,130,246,0.3)', '&:hover': { boxShadow: '0 6px 16px rgba(59,130,246,0.4)' } }}
                                        >
                                            Next
                                        </Button>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <Button
                                            fullWidth variant="outlined"
                                            type="button"
                                            onClick={() => setActiveStep(0)} disabled={isSubmitting}
                                            sx={{ borderRadius: 8, py: 1.25, fontWeight: 700, textTransform: 'none', fontSize: '1rem', color: 'text.secondary', borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            fullWidth variant="contained"
                                            color="primary"
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={isSubmitting}
                                            sx={{ borderRadius: '8px', py: 1.25, fontWeight: 700, textTransform: 'none', fontSize: '1rem', boxShadow: '0 4px 12px rgba(65,70,224,0.25)', bgcolor: '#4146e0', '&:hover': { bgcolor: '#3539bc', boxShadow: '0 6px 16px rgba(65,70,224,0.35)' } }}
                                        >
                                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Confirm Group Block'}
                                        </Button>
                                    </React.Fragment>
                                )}
                            </Box>
                        </form>

                        {/* Error toast snackbar inside modal for warnings */}
                        <Snackbar
                            open={toast.open} autoHideDuration={4000}
                            onClose={() => setToast({ ...toast, open: false })}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        >
                            <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%' }}>
                                {toast.message}
                            </Alert>
                        </Snackbar>
                    </Box>
                    </Box>
                    )}
                </Box>
            </Modal>

            {/* Corporate Duplicate Intercept Modal */}
            <Dialog open={showCorporateMatchModal} onClose={() => setShowCorporateMatchModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Duplicate Company Name Detected</DialogTitle>
                <DialogContent dividers>
                    <Typography gutterBottom>
                        An account with the name <strong>{existingCorporateMatch?.name}</strong> already exists. Is this the same company?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button
                        onClick={() => {
                            setShowCorporateMatchModal(false);
                            executeSubmission(corporateName.trim(), false, existingCorporateMatch?.id);
                        }}
                        color="inherit"
                    >
                        Yes, Link to Existing
                    </Button>
                    <Button
                        onClick={() => {
                            setShowCorporateMatchModal(false);
                            executeSubmission(corporateName.trim(), true);
                        }}
                        variant="contained" color="primary"
                    >
                        No, Create New Entity
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Guest Information Modal */}
            <Dialog open={showGuestModal} onClose={(event, reason) => {
                if (reason === 'backdropClick') return;
                handleGuestModalClose();
            }} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">Guest Information - Room {activeRoomIndexForGuestModal + 1}</Typography>
                    <IconButton onClick={handleGuestModalClose} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3, bgcolor: '#f8fafc' }}>
                    {guestModalError && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                            {guestModalError}
                        </Alert>
                    )}
                    {roomsArr[activeRoomIndexForGuestModal] && (
                        <Box>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: 'text.secondary' }}>Adult Guests</Typography>
                                {roomsArr[activeRoomIndexForGuestModal].guests.map((g, guestIndex) => {
                                    const isActive = activeGuestTab === guestIndex;
                                    const label = g.firstName ? `${g.firstName} ${g.lastName}` : `Guest ${guestIndex + 1}`;
                                    const handleChange = (field, value) => {
                                        handleGuestChange(activeRoomIndexForGuestModal, guestIndex, field, value);
                                    };

                                    return (
                                        <Accordion 
                                            key={`adult-${guestIndex}`} 
                                            expanded={isActive} 
                                            onChange={(e, isExpanded) => setActiveGuestTab(isExpanded ? guestIndex : false)} 
                                            sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' }, border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: isActive ? '#f8fafc' : '#ffffff' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                                                    <Typography fontWeight={isActive ? 700 : 500} color={isActive ? '#3b82f6' : 'text.primary'}>
                                                        {label}
                                                    </Typography>
                                                    <IconButton 
                                                        size="small" 
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setRoomsArr(prev => {
                                                                const next = [...prev];
                                                                const newGuests = [...next[activeRoomIndexForGuestModal].guests];
                                                                newGuests.splice(guestIndex, 1);
                                                                next[activeRoomIndexForGuestModal] = {
                                                                    ...next[activeRoomIndexForGuestModal],
                                                                    guests: newGuests
                                                                };
                                                                return next;
                                                            });
                                                            if (activeGuestTab === guestIndex) setActiveGuestTab(false);
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 3, pt: 2, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                                                <Box>
                                                    <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a" mb={2.5} sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <Box component="span" sx={{ width: 6, height: 20, bgcolor: '#3b82f6', borderRadius: 1 }} />
                                                            Profile Information
                                                        </Typography>
                                                        <Grid container spacing={2.5}>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField required fullWidth size="small" label="First Name" value={g.firstName || ''} onChange={(e) => handleChange('firstName', e.target.value.replace(/[^a-zA-Z\s]/g, ''))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField required fullWidth size="small" label="Last Name" value={g.lastName || ''} onChange={(e) => handleChange('lastName', e.target.value.replace(/[^a-zA-Z\s]/g, ''))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField fullWidth size="small" label="Email" value={g.email || ''} onChange={(e) => handleChange('email', e.target.value)} type="email" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                            </Grid>
                                                            <Grid item xs={12} sm={6}>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <FormControl size="small" sx={{ minWidth: 85, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                                                        <InputLabel>Code</InputLabel>
                                                                        <Select 
                                                                            value={g.phoneCode || (isUae ? '+971' : '+91')} 
                                                                            label="Code" 
                                                                            onChange={e => handleChange('phoneCode', e.target.value)}
                                                                        >
                                                                            {SUPPORTED_PHONE_CODES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                                                        </Select>
                                                                    </FormControl>
                                                                    <TextField fullWidth size="small" label="Phone Number" value={g.phone || ''} onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 15))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Paper>

                                                    <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a" mb={2.5} sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <Box component="span" sx={{ width: 6, height: 20, bgcolor: '#8b5cf6', borderRadius: 1 }} />
                                                            Address Details
                                                        </Typography>
                                                        <Grid container spacing={2.5}>
                                                            <Grid item xs={12}><TextField fullWidth size="small" label="Full Address" value={g.address || ''} onChange={(e) => handleChange('address', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                                                            <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="City" value={g.city || ''} onChange={(e) => handleChange('city', e.target.value.replace(/[^a-zA-Z\s]/g, ''))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                                                            <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="State/Province" value={g.state || ''} onChange={(e) => handleChange('state', e.target.value.replace(/[^a-zA-Z\s]/g, ''))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                                                            <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Country" value={g.country || ''} onChange={(e) => handleChange('country', e.target.value.replace(/[^a-zA-Z\s]/g, ''))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                                                            <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="ZIP/Postal Code" value={g.zip || ''} onChange={(e) => handleChange('zip', e.target.value.replace(/\D/g, ''))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                                                        </Grid>
                                                    </Paper>

                                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a" mb={2.5} sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <Box component="span" sx={{ width: 6, height: 20, bgcolor: '#10b981', borderRadius: 1 }} />
                                                            Identity Verification
                                                        </Typography>
                                                        <Grid container spacing={2.5} sx={{ mb: 3 }}>
                                                            <Grid item xs={12} sm={4}>
                                                                <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                                                    <InputLabel>ID Document Type</InputLabel>
                                                                    <Select value={g.idType || ''} label="ID Document Type" onChange={(e) => handleChange('idType', e.target.value)}>
                                                                        <MenuItem value="Aadhaar">Aadhaar</MenuItem>
                                                                        <MenuItem value="Passport">Passport</MenuItem>
                                                                        <MenuItem value="PAN Card">PAN Card</MenuItem>
                                                                        <MenuItem value="Voter ID">Voter ID</MenuItem>
                                                                        <MenuItem value="Driving Licence">Driving Licence</MenuItem>
                                                                        <MenuItem value="National ID">National ID</MenuItem>
                                                                        {g.idType && !['Aadhaar', 'Passport', 'PAN Card', 'Voter ID', 'Driving Licence', 'National ID'].includes(g.idType) && (
                                                                            <MenuItem value={g.idType}>{g.idType}</MenuItem>
                                                                        )}
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="ID Number" value={g.idNumber || ''} onChange={(e) => handleChange('idNumber', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                                                            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Nationality" value={g.nationality || ''} onChange={(e) => handleChange('nationality', e.target.value.replace(/[^a-zA-Z\s]/g, ''))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
                                                        </Grid>

                                                        <Box
                                                            onDragOver={e => { e.preventDefault(); }}
                                                            onDragLeave={(e) => { e.preventDefault(); }}
                                                            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { handleChange('idProofFile', f); handleChange('idProofPreviewUrl', URL.createObjectURL(f)); } }}
                                                            sx={{ border: `2px dashed ${'#cbd5e1'}`, borderRadius: 3, p: 4, textAlign: 'center', bgcolor: '#f8fafc', transition: 'all 0.2s', cursor: g.idProofFile ? 'default' : 'pointer', '&:hover': { borderColor: !g.idProofFile ? '#3b82f6' : '#cbd5e1', bgcolor: !g.idProofFile ? '#eff6ff' : '#f8fafc' } }}
                                                            onClick={() => {
                                                                if (!g.idProofFile) {
                                                                    const fileInput = document.createElement('input');
                                                                    fileInput.type = 'file';
                                                                    fileInput.accept = 'image/jpeg,image/png,application/pdf';
                                                                    fileInput.onchange = (e) => {
                                                                        if (e.target.files && e.target.files.length > 0) {
                                                                            const file = e.target.files[0];
                                                                            handleChange('idProofFile', file);
                                                                            handleChange('idProofPreviewUrl', URL.createObjectURL(file));
                                                                        }
                                                                    };
                                                                    fileInput.click();
                                                                }
                                                            }}
                                                        >
                                                            {g.idProofPreviewUrl && (!g.idProofFile || g.idProofFile.type.startsWith('image/')) && !g.idProofPreviewUrl.toLowerCase().includes('.pdf') ? (
                                                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                                    <Box component="img" src={g.idProofPreviewUrl} alt="ID Preview" sx={{ maxWidth: '100%', maxHeight: 180, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', objectFit: 'contain', display: 'block', mx: 'auto' }} />
                                                                    <Box onClick={(e) => { e.stopPropagation(); handleChange('idProofFile', null); handleChange('idProofPreviewUrl', null); }} sx={{ position: 'absolute', top: -10, right: -10, width: 28, height: 28, bgcolor: '#ef4444', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 16, fontWeight: 'bold', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', '&:hover': { bgcolor: '#dc2626' } }}>×</Box>
                                                                </Box>
                                                            ) : g.idProofPreviewUrl && (g.idProofPreviewUrl.toLowerCase().includes('.pdf') || (g.idProofFile && g.idProofFile.type === 'application/pdf')) ? (
                                                                <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                                                                    <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>PDF Document Attached</Typography>
                                                                    <Button variant="outlined" size="small" onClick={(e) => { e.stopPropagation(); window.open(g.idProofPreviewUrl, '_blank'); }}>View PDF</Button>
                                                                    <Box onClick={(e) => { e.stopPropagation(); handleChange('idProofFile', null); handleChange('idProofPreviewUrl', null); }} sx={{ position: 'absolute', top: -10, right: -10, width: 28, height: 28, bgcolor: '#ef4444', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 16, fontWeight: 'bold', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', '&:hover': { bgcolor: '#dc2626' } }}>×</Box>
                                                                </Box>
                                                            ) : (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                                                    <CloudUploadIcon sx={{ fontSize: 48, color: g.idProofFile ? '#3b82f6' : '#94a3b8' }} />
                                                                    <Typography variant="body1" fontWeight={600} color={g.idProofFile ? '#0f172a' : 'text.primary'}>{g.idProofFile ? g.idProofFile.name : 'Click or drag to upload ID document'}</Typography>
                                                                    {!g.idProofFile && <Typography variant="body2" color="text.secondary">Supports JPG, PNG, PDF (Max 5MB)</Typography>}
                                                                    {g.idProofFile && (
                                                                        <Button variant="outlined" size="small" sx={{ mt: 1.5, borderRadius: 2, textTransform: 'none', borderColor: '#e2e8f0', color: 'text.secondary', '&:hover': { bgcolor: '#f8fafc' } }} onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const fileInput = document.createElement('input');
                                                                            fileInput.type = 'file';
                                                                            fileInput.accept = 'image/jpeg,image/png,application/pdf';
                                                                            fileInput.onchange = (ev) => {
                                                                                if (ev.target.files && ev.target.files.length > 0) {
                                                                                    const file = ev.target.files[0];
                                                                                    handleChange('idProofFile', file);
                                                                                    handleChange('idProofPreviewUrl', URL.createObjectURL(file));
                                                                                }
                                                                            };
                                                                            fileInput.click();
                                                                        }}>
                                                                            Replace Document
                                                                        </Button>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Paper>
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                                {roomsArr[activeRoomIndexForGuestModal].guests.length < Number(roomsArr[activeRoomIndexForGuestModal].adultsCount) && (
                                    <Button 
                                        variant="outlined" size="small" 
                                        startIcon={<PersonAddIcon />}
                                        onClick={() => {
                                            setRoomsArr(prev => {
                                                const next = [...prev];
                                                next[activeRoomIndexForGuestModal] = {
                                                    ...next[activeRoomIndexForGuestModal],
                                                    guests: [...next[activeRoomIndexForGuestModal].guests, { ...initialGuestState }]
                                                };
                                                return next;
                                            });
                                            setActiveGuestTab(roomsArr[activeRoomIndexForGuestModal].guests.length);
                                        }} 
                                        sx={{ mt: 1, fontWeight: 'bold', borderRadius: 2 }}
                                    >
                                        Add Guest
                                    </Button>
                                )}
                            </Box>

                            {/* Kids Information Section */}
                            {Number(roomsArr[activeRoomIndexForGuestModal].kidsCount) > 0 && (
                                <Box sx={{ mt: 4 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" mb={2} color="text.secondary">Child Guests</Typography>
                                    {Array.from({ length: Number(roomsArr[activeRoomIndexForGuestModal].kidsCount) }).map((_, kIdx) => {
                                        const kid = (roomsArr[activeRoomIndexForGuestModal].kids && roomsArr[activeRoomIndexForGuestModal].kids[kIdx]) ? roomsArr[activeRoomIndexForGuestModal].kids[kIdx] : { ...initialKidState };
                                        const isKidActive = activeGuestTab === `kid-${kIdx}`;
                                        const label = kid.firstName ? `${kid.firstName} ${kid.lastName}` : `Child ${kIdx + 1}`;
                                        
                                        const handleKidChange = (field, value) => {
                                            setRoomsArr(prev => {
                                                const next = [...prev];
                                                const room = { ...next[activeRoomIndexForGuestModal] };
                                                let kidsArr = room.kids ? [...room.kids] : [];
                                                
                                                while (kidsArr.length <= kIdx) {
                                                    kidsArr.push({ ...initialKidState });
                                                }
                                                
                                                kidsArr[kIdx] = { ...kidsArr[kIdx], [field]: value };
                                                room.kids = kidsArr;
                                                next[activeRoomIndexForGuestModal] = room;
                                                
                                                return next;
                                            });
                                        };

                                        return (
                                            <Accordion 
                                                key={`kid-${kIdx}`} 
                                                expanded={isKidActive} 
                                                onChange={(e, isExpanded) => setActiveGuestTab(isExpanded ? `kid-${kIdx}` : false)} 
                                                sx={{ mb: 2, borderRadius: '8px !important', '&:before': { display: 'none' }, border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}
                                            >
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: isKidActive ? '#f8fafc' : '#ffffff' }}>
                                                    <Typography fontWeight={isKidActive ? 700 : 500} color={isKidActive ? '#3b82f6' : 'text.primary'}>
                                                        {label}
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 3, pt: 2, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                                                    <Grid container spacing={2.5}>
                                                        <Grid item xs={12} sm={3}>
                                                            <TextField fullWidth size="small" label="First Name" value={kid.firstName || ''} onChange={e => handleKidChange('firstName', e.target.value.replace(/[^a-zA-Z\s]/g, ''))} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={3}>
                                                            <TextField fullWidth size="small" label="Last Name" value={kid.lastName || ''} onChange={e => handleKidChange('lastName', e.target.value.replace(/[^a-zA-Z\s]/g, ''))} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={2}>
                                                            <TextField fullWidth size="small" label="Age" type="number" inputProps={{ min: 0, max: 12 }} value={kid.age || ''} onChange={e => handleKidChange('age', e.target.value)} />
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <TextField fullWidth size="small" label="Date of Birth" type="date" InputLabelProps={{ shrink: true }} inputProps={{ min: new Date(new Date().setFullYear(new Date().getFullYear() - 13)).toISOString().split('T')[0], max: new Date().toISOString().split('T')[0] }} value={kid.dob || ''} onChange={e => handleKidChange('dob', e.target.value)} />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant="subtitle2" fontWeight={700} color="#0f172a" mb={1.5} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box component="span" sx={{ width: 4, height: 16, bgcolor: '#10b981', borderRadius: 1 }} />
                                                                ID Proof (Optional)
                                                            </Typography>
                                                            <Box
                                                                onDragOver={e => { e.preventDefault(); }}
                                                                onDragLeave={(e) => { e.preventDefault(); }}
                                                                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { handleKidChange('idProofFile', f); handleKidChange('idProofPreviewUrl', URL.createObjectURL(f)); } }}
                                                                sx={{ border: `2px dashed ${'#cbd5e1'}`, borderRadius: 3, p: 4, textAlign: 'center', bgcolor: '#f8fafc', transition: 'all 0.2s', cursor: kid.idProofFile ? 'default' : 'pointer', '&:hover': { borderColor: !kid.idProofFile ? '#3b82f6' : '#cbd5e1', bgcolor: !kid.idProofFile ? '#eff6ff' : '#f8fafc' } }}
                                                                onClick={() => {
                                                                    if (!kid.idProofFile) {
                                                                        const fileInput = document.createElement('input');
                                                                        fileInput.type = 'file';
                                                                        fileInput.accept = 'image/jpeg,image/png,application/pdf';
                                                                        fileInput.onchange = (e) => {
                                                                            if (e.target.files && e.target.files.length > 0) {
                                                                                const file = e.target.files[0];
                                                                                handleKidChange('idProofFile', file);
                                                                                handleKidChange('idProofPreviewUrl', URL.createObjectURL(file));
                                                                            }
                                                                        };
                                                                        fileInput.click();
                                                                    }
                                                                }}
                                                            >
                                                                {kid.idProofPreviewUrl && (!kid.idProofFile || kid.idProofFile.type.startsWith('image/')) && !kid.idProofPreviewUrl.toLowerCase().includes('.pdf') ? (
                                                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                                        <Box component="img" src={kid.idProofPreviewUrl} alt="ID Preview" sx={{ maxWidth: '100%', maxHeight: 180, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', objectFit: 'contain', display: 'block', mx: 'auto' }} />
                                                                        <Box onClick={(e) => { e.stopPropagation(); handleKidChange('idProofFile', null); handleKidChange('idProofPreviewUrl', null); }} sx={{ position: 'absolute', top: -10, right: -10, width: 28, height: 28, bgcolor: '#ef4444', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 16, fontWeight: 'bold', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', '&:hover': { bgcolor: '#dc2626' } }}>×</Box>
                                                                    </Box>
                                                                ) : kid.idProofPreviewUrl && (kid.idProofPreviewUrl.toLowerCase().includes('.pdf') || (kid.idProofFile && kid.idProofFile.type === 'application/pdf')) ? (
                                                                    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                                                                        <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>PDF Document Attached</Typography>
                                                                        <Button variant="outlined" size="small" onClick={(e) => { e.stopPropagation(); window.open(kid.idProofPreviewUrl, '_blank'); }}>View PDF</Button>
                                                                        <Box onClick={(e) => { e.stopPropagation(); handleKidChange('idProofFile', null); handleKidChange('idProofPreviewUrl', null); }} sx={{ position: 'absolute', top: -10, right: -10, width: 28, height: 28, bgcolor: '#ef4444', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 16, fontWeight: 'bold', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', '&:hover': { bgcolor: '#dc2626' } }}>×</Box>
                                                                    </Box>
                                                                ) : (
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                                                        <CloudUploadIcon sx={{ fontSize: 48, color: kid.idProofFile ? '#3b82f6' : '#94a3b8' }} />
                                                                        <Typography variant="body1" fontWeight={600} color={kid.idProofFile ? '#0f172a' : 'text.primary'}>{kid.idProofFile ? kid.idProofFile.name : 'Click or drag to upload ID document'}</Typography>
                                                                        {!kid.idProofFile && <Typography variant="body2" color="text.secondary">Supports JPG, PNG, PDF (Max 5MB)</Typography>}
                                                                        {kid.idProofFile && (
                                                                            <Button variant="outlined" size="small" sx={{ mt: 1.5, borderRadius: 2, textTransform: 'none', borderColor: '#e2e8f0', color: 'text.secondary', '&:hover': { bgcolor: '#f8fafc' } }} onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                const fileInput = document.createElement('input');
                                                                                fileInput.type = 'file';
                                                                                fileInput.accept = 'image/jpeg,image/png,application/pdf';
                                                                                fileInput.onchange = (ev) => {
                                                                                    if (ev.target.files && ev.target.files.length > 0) {
                                                                                        const file = ev.target.files[0];
                                                                                        handleKidChange('idProofFile', file);
                                                                                        handleKidChange('idProofPreviewUrl', URL.createObjectURL(file));
                                                                                    }
                                                                                };
                                                                                fileInput.click();
                                                                            }}>
                                                                                Replace Document
                                                                            </Button>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })}
                                </Box>
                            )}

                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 2, pl: 1 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {roomsArr[activeRoomIndexForGuestModal]?.guests?.length || 0} / {roomsArr[activeRoomIndexForGuestModal]?.adultsCount || 1} adults
                        </Typography>
                        {Number(roomsArr[activeRoomIndexForGuestModal]?.kidsCount) > 0 && (
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                {(roomsArr[activeRoomIndexForGuestModal]?.kids || []).filter(k => k.firstName || k.lastName).length} / {roomsArr[activeRoomIndexForGuestModal]?.kidsCount} kids
                            </Typography>
                        )}
                    </Box>
                    <Button onClick={handleGuestModalClose} variant="contained" color="primary">
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
